import { IoLocationSharp } from "react-icons/io5";
import { FaTrashAlt } from "react-icons/fa";
import Layout from "../components/global/layout";
import timezoneModel from "../models/timezone.model";
import { useNavigate, useParams, Link } from "react-router-dom";
import loader from "../methods/loader";
import ApiClient from "../methods/api/apiClient";
import datepipeModel from "../models/datepipemodel";
import { Tooltip } from "antd";
import { BsCheck, BsCrosshair } from "react-icons/bs";
import Modal from "../components/common/Modal";
import AddAttendee from "./Events/AddAttendee";
import { toast } from "react-toastify";
import Members from "../components/Events/Member";
import rolesModel from "../models/roles.model";
import { FiPrinter, FiUser } from "react-icons/fi";
import { MdInsertLink, MdOutlineShare } from "react-icons/md";
import Groups from "./Groups";
import { AiOutlineUsergroupAdd } from "react-icons/ai";
import { useSelector } from "react-redux";
import MemberHistory from "./Events/MemberHistory";
import RequestGroup from "./Events/RequestGroup";
import { LiaUserClockSolid } from "react-icons/lia";
import Swal from 'sweetalert2';
import { decryptId, encryptId } from "../components/common/Encryption/encryption";
import RegistrationCard from "./RegistrationList/RegistrationCard";
import { RiRestTimeLine } from "react-icons/ri";
import { FaHandshake, FaStar, FaLock } from 'react-icons/fa';
import environment from "../environment";
import React, { useEffect, useState } from "react";
import ThankYouManager from './Events/ThankYouManager';

const ManagerRequestForm = ({ attendee, allAttendees, eventId, groupId, user, onComplete, connectMeetLabel }: any) => {
  const [selectedOption, setSelectedOption] = useState(attendee?.connectMeet || "system_generate");
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [acList, setAcList] = useState<any[]>([]); // attendee's Already Connected list

  // Fetch existing request for this attendee
  useEffect(() => {
    const fetchExistingRequest = async () => {
      try {
        const email = attendee?.memberDetails?.email || attendee?.email;
        if (email && eventId) {
          const res = await ApiClient.get("api/group-request/list", {
            eventId: eventId,
            requestedBy: email
          });
          
          if (res?.success && res?.data?.length > 0) {
            const existingRequest = res.data[0];
            setSelectedOption(existingRequest.requestType || "system_generate");
            
            if (existingRequest.attendeesDetails && existingRequest.attendeesDetails.length > 0) {
              const existingIds = existingRequest.attendeesDetails
                .filter((a: any) => a?._id)
                .map((a: any) => a._id.toString());
              setSelectedMembers(existingIds);
            }
          } else {
            // cw: mark default to Available in the Manage ${connectMeetLabel} Request button
            setSelectedOption("system_generate");
          }
        }
      } catch (err) {
        console.error("Failed to fetch existing request:", err);
        // cw: On error, default to Available
        setSelectedOption("system_generate");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchExistingRequest();
  }, [attendee, eventId]);

  // cw: Fetch attendee's Already Connected list
  useEffect(() => {
    const userId = attendee?.userId || attendee?.memberDetails?.userId || attendee?.memberDetails?._id;
    if (userId && groupId) {
      ApiClient.get(`api/exclusions/list?groupId=${groupId}&userId=${userId}`).then((res: any) => {
        if (res?.success && res?.data) {
          setAcList(res.data);
        }
      }).catch(() => {});
    }
  }, [attendee, groupId]);

  // cw: Check if a member is on the attendee's AC list (by userId)
  const isOnAcList = (userId: string) => {
    if (!userId) return false;
    return acList.some((excl: any) =>
      excl.excludedUserId?._id?.toString() === userId?.toString() ||
      excl.excludedUserId?.toString() === userId?.toString()
    );
  };

  // cw: Get the AC exclusion record for a member (by userId)
  const getAcExclusion = (userId: string) => {
    if (!userId) return null;
    return acList.find((excl: any) =>
      excl.excludedUserId?._id?.toString() === userId?.toString() ||
      excl.excludedUserId?.toString() === userId?.toString()
    );
  };

  // cw: Handle checkbox toggle with AC list check
  const handleMemberToggle = (memberId: string, memberName: string, checked: boolean, memberUserId?: string) => {
    if (checked && memberUserId && isOnAcList(memberUserId)) {
      const excl = getAcExclusion(memberUserId);
      const userId = attendee?.userId || attendee?.memberDetails?.userId || attendee?.memberDetails?._id;
      Swal.fire({
        title: 'Already Connected',
        text: `${attendee?.memberDetails?.fullName || attendee?.fullName || 'This attendee'} has ${memberName} on their Already Connected list.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Remove From AC',
        cancelButtonText: 'Cancel Request',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
      }).then((result) => {
        if (result.isConfirmed && excl) {
          // Remove from AC list
          ApiClient.put('api/exclusions/remove', { id: excl._id, userId }).then((res: any) => {
            if (res?.success) {
              setAcList(acList.filter((e: any) => e._id !== excl._id));
              setSelectedMembers([...selectedMembers, memberId]);
              toast.success('Removed from Already Connected list');
            } else {
              toast.error(res?.message || 'Failed to remove from AC list');
            }
          }).catch(() => toast.error('Failed to remove from AC list'));
        }
        // If cancelled, don't add the member
      });
    } else if (checked) {
      setSelectedMembers([...selectedMembers, memberId]);
    } else {
      setSelectedMembers(selectedMembers.filter(id => id !== memberId));
    }
  };

  const availableMembers = allAttendees
    .filter((itm: any) => {
      const email = itm?.memberDetails?.email || itm?.email;
      const attendeeEmail = attendee?.memberDetails?.email || attendee?.email;
      return email !== attendeeEmail;
    })
    .map((itm: any) => ({
      id: itm.id || itm._id,
      name: itm?.memberDetails?.fullName || itm?.fullName || 'Guest',
      email: itm?.memberDetails?.email || itm?.email,
      userId: itm?.memberDetails?._id || itm?.userId,
      raw: itm, // full attendee row (memberDetails) for the photolist card
    }))
    .sort((a: any, b: any) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()));

  const handleSubmit = () => {
    if (selectedOption === "connect_with_members" && selectedMembers.length === 0) {
      toast.error("Please select at least one member");
      return;
    }
  
    setIsSubmitting(true);
    
    const email = attendee?.memberDetails?.email || attendee?.email;
    
    // cw: Fetch the actual attendee record to get the userId
    ApiClient.get("api/attendees/list", { 
      eventId: eventId, 
      email: email 
    }).then(attendeeRes => {
      let odlUserId = null;
      
      if (attendeeRes?.success && attendeeRes?.data?.length > 0) {
        const actualAttendee = attendeeRes.data[0];
        odlUserId = actualAttendee.userId;
        console.log("Found userId from DB:", odlUserId);
      }
      
      // Fallback: try to get userId from attendee prop
      if (!odlUserId) {
        if (attendee?.userId) {
          odlUserId = attendee.userId;
        } else if (attendee?.memberDetails?.userId) {
          odlUserId = attendee.memberDetails.userId;
        }
        // Guests won't have userId - that's okay, backend will handle by email
      }
      
      const payload: any = {
        eventId: eventId,
        groupId: groupId,
        requestedBy: email,
        attendeeIds: selectedMembers,
        requestType: selectedOption,
        managedBy: user?.email,
        attendeeId: attendee?._id || attendee?.id  // Always include attendeeId
      };
      
      // Only include userId if it exists (for registered users)
      if (odlUserId) {
        payload.userId = odlUserId;
      }

      console.log("Submitting payload:", payload);
  
      // Update group request
      ApiClient.post("api/group/request", payload).then(res => {
        ApiClient.put("api/attendees/update", {
          id: attendee?.id || attendee?._id,
          connectMeet: selectedOption
        }).then(() => {
          setIsSubmitting(false);
          if (res) {
            toast.success(res.message || `${connectMeetLabel} request updated`);
            onComplete();
          }
        }).catch(err => {
          setIsSubmitting(false);
          console.error("Failed to update attendee:", err);
          toast.error("Failed to update attendee status");
        });
      }).catch(err => {
        setIsSubmitting(false);
        console.error("Failed to update request:", err);
        toast.error("Failed to update request");
      });
    }).catch(err => {
      setIsSubmitting(false);
      console.error("Failed to fetch attendee:", err);
      toast.error("Failed to fetch attendee details");
    });
  };

  if (isLoading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-gray-50 p-3 rounded">
        <p className="text-sm"><strong>Attendee:</strong> {attendee?.memberDetails?.fullName || attendee?.fullName}</p>
        <p className="text-sm"><strong>Email:</strong> {attendee?.memberDetails?.email || attendee?.email}</p>
        <p className="text-sm text-gray-600"><strong>Type:</strong> {attendee?.type || 'Member'}</p>
      </div>

      <div className="flex flex-col gap-3">
        <label className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
          <input
            type="radio"
            name="requestType"
            checked={selectedOption === "system_generate"}
            onChange={() => setSelectedOption("system_generate")}
            className="h-4 w-4"
          />
          <span>I'm available for {'aeiouAEIOU'.includes(connectMeetLabel?.[0]) ? 'an' : 'a'} {connectMeetLabel} for this event</span>
        </label>

        <label className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
          <input
            type="radio"
            name="requestType"
            checked={selectedOption === "connect_with_members"}
            onChange={() => setSelectedOption("connect_with_members")}
            className="h-4 w-4"
          />
         <span>Request specific people for my {connectMeetLabel} for this event</span>
        </label>

        {selectedOption === "connect_with_members" && (
          <div className="ml-6 p-3 bg-gray-50 rounded">
            <p className="text-sm mb-2 font-medium">Select the people who RSVP'd Yes to request:</p>
            <div className="max-h-96 overflow-y-auto flex flex-col gap-2 pr-1">
              {availableMembers.map((member: any) => {
                const isSel = selectedMembers.includes(member.id);
                return (
                  <RegistrationCard
                    key={member.id}
                    member={member.raw}
                    connectMeetLabel={connectMeetLabel}
                    testimonialLabel={''}
                    canAct={false}
                    selectable
                    selected={isSel}
                    onToggleSelect={() => handleMemberToggle(member.id, member.name, !isSel, member.userId)}
                    onRequestCm={() => {}}
                    onRequestTy={() => {}}
                  />
                );
              })}
            </div>
            {availableMembers.length === 0 && (
              <p className="text-sm text-gray-500">No other attendees available</p>
            )}
          </div>
        )}

        <label className="flex items-center gap-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
          <input
            type="radio"
            name="requestType"
            checked={selectedOption === "not_interested"}
            onChange={() => setSelectedOption("not_interested")}
            className="h-4 w-4"
          />
          <span>I'm NOT available for {'aeiouAEIOU'.includes(connectMeetLabel?.[0]) ? 'an' : 'a'} {connectMeetLabel} for this event</span>
        </label>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <button
          onClick={onComplete}
          className="px-4 py-2 border rounded-lg hover:bg-gray-100"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 bg-[#EF7A2B] text-white rounded-lg hover:bg-orange-600"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Saving..." : "Save Request"}
        </button>
      </div>
    </div>
  );
};

const EventDetail = () => {
  interface NewDataItem {
    email: string;
  }
  const [host, setHost]: any = useState()
  const [data, setData]: any = useState()
  const [attendee, setAttendee]: any = useState([])
  const [aloading, setALoader] = useState(false)
  const [isModal, setModal] = useState('')
  const [zoomImportOpen, setZoomImportOpen] = useState(false)
  const [zoomBusy, setZoomBusy] = useState(false)
  const [zoomSummary, setZoomSummary] = useState<any>(null)
  const [photoImportOpen, setPhotoImportOpen] = useState(false)
  const [photoMembers, setPhotoMembers] = useState<any[]>([])
  const [photoGuests, setPhotoGuests] = useState<any[]>([])
  const photoFileRef = React.useRef<HTMLInputElement>(null)
  const [isRModal, setRModal] = useState(false)
  const [isRsvpStatusModal, setRsvpStatusModal] = useState(false)
  // cw: calendar download shown when the logged-in user's RSVP is Yes (public api/event/ics).
  const [cal, setCal] = useState<{ ics: string; gcalUrl: string; event: any } | null>(null)
  const [group, setGroup] = useState(false)
  const [memberHistory, setmemberHistory] = useState(false)
  const [invites, setInvites] = useState([])
  const [requestGroup, setRequestGroup] = useState(false)
  const [memberHistoryData, setmemberHistoryData] = useState()
  const [attendeesGroup, setattendeesGroup] = useState()
  const [newData, setNewData] = useState([])
  const [Roledata, setRoleData]: any = useState()
  const user = useSelector((state: any) => state.user);
  const history = useNavigate()
  const navigate = useNavigate();
  const [role, setRole] = useState()
  const [me, setMe] = useState()
  const [member, setMember]: any = useState()
  // resolve event id safely
  const { eventid: eventidParam } = useParams();
  const [id, setId] = useState<string | null>(null);
  const connectMeetLabel = data?.groupId?.connectMeetLabel || user?.connectMeetLabel || 'ConnectMeet';
  const pluralize = (label: string) => {
    if (label.endsWith('s')) return label;
    const words = label.split(' ');
    const last = words[words.length - 1];
    const pluralLast = last.endsWith('y') ? last.slice(0, -1) + 'ies' : last + 's';
    return [...words.slice(0, -1), pluralLast].join(' ');
  };
  const connectMeetLabelPlural = pluralize(connectMeetLabel);
  const testimonialLabel = user?.testimonialLabel || 'Thank You';
  const testimonialLabelPlural = pluralize(testimonialLabel);
  const guestFormDirtyRef = React.useRef(false);
  const memberFormDirtyRef = React.useRef(false);
  const [showManagerRequestModal, setShowManagerRequestModal] = useState(false);
  const [selectedAttendeeForRequest, setSelectedAttendeeForRequest] = useState<any>(null);
  const [attendeeRequestStatuses, setAttendeeRequestStatuses] = useState<{[email: string]: string}>({});
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [showSubmitThankYou, setShowSubmitThankYou] = useState(false);
  const [tyReceiverId, setTyReceiverId] = useState('');
  const [tyType, setTyType] = useState('');
  const [tyNote, setTyNote] = useState('');
  const [tySubmitting, setTySubmitting] = useState(false);
  const [tyDrafting, setTyDrafting] = useState(false);
  const [showOwnVoice, setShowOwnVoice] = useState(false);
  const [peopleTab, setPeopleTab] = useState('going');
  const [peopleSearch, setPeopleSearch] = useState('');
  const [showQrModal, setShowQrModal] = useState(false);
  const [moreToolsOpen, setMoreToolsOpen] = useState(false);
  const [selectedStep, setSelectedStep] = useState(1);

  // cw: Thank You writer — draft from the recipient's profile (backend recipe) + whatever context the
  // user typed. Fills the note; the user can edit or clear it. No regenerate button.
  const handleDraftThankYou = async () => {
    if (!tyReceiverId) { toast.error('Select who you\'re thanking first'); return; }
    setTyDrafting(true);
    try {
      // hideError=true so ApiClient doesn't also toast — we show exactly one message here.
      const res: any = await ApiClient.post('api/testimonial/draft', { receivedById: tyReceiverId, context: tyNote || '' }, '', true);
      const body = res?.data && typeof res.data === 'object' ? res.data : res;
      if (res?.success && body.draft) setTyNote(body.draft);
      else toast.error(res?.message || 'Could not draft');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not draft');
    }
    setTyDrafting(false);
  };

  // Premium "write in my own voice" — opens a popup showing the upload + paste boxes, which are
  // visible but locked behind the $2/month subscription.
  const handleOwnVoiceUpsell = () => setShowOwnVoice(true);
  const handleSubscribeOwnVoice = () => { toast('Payment setup coming soon.'); };
  const [tySharedGroups, setTySharedGroups] = useState<any[]>([]);
  const [tySelectedGroupIds, setTySelectedGroupIds] = useState<string[]>([]);

  useEffect(() => {
    if (!eventidParam) {
      setId(null);
      return;
    }

    (async () => {
      // try decodeURIComponent in case it's URL-encoded
      let rawParam = eventidParam;
      try {
        rawParam = decodeURIComponent(eventidParam);
      } catch (e) {
        rawParam = eventidParam;
      }

      // Try decryptId (resilient decrypt helper should exist). If decryptId returns a valid 24-hex id, use it.
      try {
        const maybe = await decryptId(rawParam); // decryptId now resilient
        if (maybe && /^[0-9a-fA-F]{24}$/.test(maybe)) {
          setId(maybe);
        } else if (maybe) {
          // decrypted but not necessarily a 24-hex id — still set it for APIs that accept other ids
          setId(maybe);
        } else if (/^[0-9a-fA-F]{24}$/.test(rawParam)) {
          // fallback: the param itself is a raw ObjectId
          setId(rawParam);
        } else {
          console.error("Could not resolve event id from param:", eventidParam);
          setId(null);
        }
      } catch (err) {
        // decrypt threw; fallback to raw param if it looks like ObjectId
        if (/^[0-9a-fA-F]{24}$/.test(rawParam)) {
          setId(rawParam);
        } else {
          console.error("decryptId threw and param is not valid ObjectId:", rawParam, err);
          setId(null);
        }
      }
    })();
  }, [eventidParam]);

  const [loadingMessage, setLoadingMessage] = useState(<div></div>);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState('');
  
  const getDetail = () => {
    loader(true)
    ApiClient.get('api/event/details', { id: id, email: user?.email }).then(res => {
      loader(false)
      if (res.success) {
        setData(res.data)
        if (res.data?.addedBy?._id)
          // getHostDetail(res.data.addedBy._id)
          if (res.data?.groupId?._id) getMember(res.data.groupId._id)

      }
    })
  }

  // const getHostDetail = (id: any) => {
  //   ApiClient.get('api/user/detail', { id: id }).then(res => {
  //     if (res.success) {
  //       setHost(res.data)
  //     }
  //   })
  // }

  // cw: Import Attendance from Zoom — read participants of a completed Zoom meeting (read-only)
  // and mark matching event attendees present. No Zoom meeting is created or modified.
  const [zoomMeetingIdInput, setZoomMeetingIdInput] = useState('')
  // cw: Zoom participants not on any list — the EC can add them as guests (Save) or drop each (✕).
  const [zoomAddGuests, setZoomAddGuests] = useState<any[]>([])

  const openZoomImport = () => {
    setZoomSummary(null)
    setZoomAddGuests([])
    setZoomMeetingIdInput('')
    setZoomImportOpen(true)
  }

  const runZoomImport = (meetingId: any) => {
    const groupId = data?.groupId?._id || data?.groupId
    setZoomBusy(true)
    ApiClient.post('api/zoom/mark-attendance', { groupId, eventId: id, meetingId }).then((res: any) => {
      setZoomBusy(false)
      if (res?.success) {
        setZoomSummary(res)
        // Unmatched participants (with an email) become the add-as-guest list.
        const um = Array.isArray(res.unmatched) ? res.unmatched : []
        const normalized = um
          .map((u: any) => (typeof u === 'string' ? { name: u, email: '' } : u))
          .filter((u: any) => u && u.email)
        setZoomAddGuests(normalized)
        getAttendee()
      } else {
        toast.error(res?.message || 'Failed to import attendance from Zoom')
      }
    }).catch(() => { setZoomBusy(false); toast.error('Failed to import attendance from Zoom') })
  }

  const updateZoomGuest = (index: number, field: 'name' | 'email', value: string) => {
    setZoomAddGuests(prev => prev.map((g: any, i: number) => (i === index ? { ...g, [field]: value } : g)))
  }

  const removeZoomGuest = (index: number) => {
    setZoomAddGuests(prev => prev.filter((_: any, i: number) => i !== index))
  }

  // Single Save: adds every remaining guest (after the GL/AGL/CMM's edits and ✕s), then closes.
  const saveZoomGuests = async () => {
    const groupId = data?.groupId?._id || data?.groupId
    const toAdd = zoomAddGuests.filter((g: any) => (g.email || '').trim())
    setZoomBusy(true)
    let added = 0
    for (const g of toAdd) {
      try {
        const res: any = await ApiClient.post('api/add/guest/attendee', {
          eventId: id, groupId, email: (g.email || '').trim(), fullName: (g.name || '').trim() || g.email, isPresent: true, meetConfirm: 'Yes'
        })
        if (res?.success) added++
      } catch (e) { /* skip failures (e.g. already added) */ }
    }
    setZoomBusy(false)
    if (toAdd.length) toast.success(`Added ${added} guest(s)`)
    setZoomAddGuests([])
    getAttendee()
    setZoomImportOpen(false)
  }

  // cw: match an attendee row to the logged-in user. Email alone is fragile (case/whitespace, or a
  // redux user missing email), so also match on the stable user id against userId/memberId/member _id.
  const emailsEqual = (a?: string, b?: string) =>
    !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
  const matchesCurrentUser = (itm: any) =>
    emailsEqual(itm?.memberDetails?.email || itm?.email, user?.email) ||
    (!!user?._id && (
      String(itm?.userId) === String(user._id) ||
      String(itm?.memberId) === String(user._id) ||
      String(itm?.memberDetails?._id) === String(user._id)
    ));

  const getAttendee = () => {
    let f = {
      eventId: id,
      joinRequest: '',
    }
    setALoader(true)
    ApiClient.get('api/attendees/list', f).then(res => {
      setALoader(false)
      if (res.success) {
        setNewData(res?.data)
        let data: any = res.data.map((itm: any) => {
          return {
            ...itm,
            email: itm?.memberDetails?.email || itm.email
          }
        })

        setAttendee(data)
        let ext = data.find((itm: any) => matchesCurrentUser(itm))
        if (ext) {

          // cw: trying to get the actual role name for GLs
          // console.log("Found current user attendee:", ext);
          // console.log("User type:", ext.type);
          // console.log("Member details role:", ext.memberDetails?.role);
          // console.log("Attendee role:", ext.attendeeRole);

          // cw: INCORRECT -- og supposed to give everyone without memberDetails the "member" role
          // cw: but this doesnt consider the case of group leaders since theyre not added to the addmembers table, thus losing their gl permissions
          //setRole(ext?.memberDetails?.role || 'member')

          // cw: check the type value in attendees table. {type: "Group Leader"}. If there's nothing, default to the "member" role
          setRole(ext?.type || ext?.memberDetails?.role || 'member')
          setMe(ext)
        }
      }
    })
  }

  // cw: auto-refresh attendee list every 4 seconds for live check-in updates
  // only update state if data has actually changed to prevent UI shifting
  // pauses while any modal is open to avoid re-render conflicts
  useEffect(() => {
    if (!id) return;
    if (isRModal || showThankYouModal || showManagerRequestModal || showSubmitThankYou) return;

    const interval = setInterval(() => {
      ApiClient.get('api/attendees/list', { eventId: id, joinRequest: '' }).then(res => {
        if (res.success) {
          let newData = res.data.map((itm: any) => ({
            ...itm,
            email: itm?.memberDetails?.email || itm.email
          }));

          // Only update state if data has actually changed
          setAttendee((prevAttendee: any[]) => {
            const prevIds = prevAttendee.map((a: any) => `${a.id || a._id}-${a.isPresent}-${a.meetConfirm}`).sort().join(',');
            const newIds = newData.map((a: any) => `${a.id || a._id}-${a.isPresent}-${a.meetConfirm}`).sort().join(',');

            if (prevIds !== newIds) {
              return newData;
            }
            return prevAttendee;
          });
        }
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [id, isRModal, showThankYouModal, showManagerRequestModal, showSubmitThankYou]);

  const getInvites = () => {
    let f = {
      eventId: id,
      joinRequest: 'pending',
    }
    ApiClient.get('api/attendees/list', f).then(res => {
      if (res.success) {
        setInvites(res.data)
      }
    })
  }


  // const getAttendeesGroupPair = () => {
  //   // loader(true)
  //   ApiClient.get(`api/event/groups?eventId=${id}`).then(res => {
  //     if (res.success) {
  //       setattendeesGroup(res?.data)
  //       const stateData = {
  //         eventDetail: { attendeesGroup },
  //         dataa: { data },
  //         eventId: { id },
  //         attendeesGroup: attendeesGroup

  //       }
  //       history(`/groupsDetail/${encryptId(id)}`, { state: stateData });
  //       // loader(false)
  //     }
  //   })
  // }
  const getAttendeesGroupPair = () => {
    setLoading(true);
    setLoadingMessage(<div className="h-32 flex items-center justify-center">
      <div className="loader_shine"></div>
      <p>Generating groups...</p>
    </div>);

    ApiClient.get(`api/event/groups?eventId=${id}`).then(async (res) => {
      if (res.success) {
        const encryptedId = await encryptId(id);
        setTimeout(() => {
          history(`/troika-assignments/${encryptedId}`);
        }, 100);
      } else {
        // cw: Show specific error for only one person checked in
        if (res.error?.message?.includes("one person") || res.message?.includes("one person")) {
          toast.error(`${connectMeetLabelPlural} cannot be generated with one or less people checked in.`);
        } else {
          toast.error(res.message || res.error?.message || `Failed to generate ${connectMeetLabelPlural}`);
        }
      }
      setLoading(false);
    }).catch((error) => {
      console.error('Error:', error);
      const errorMsg = error?.response?.data?.error?.message || error?.response?.data?.message || '';
      if (errorMsg.includes("one person")) {
        toast.error(`${connectMeetLabelPlural} cannot be generated with one or less people checked in.`);
      } else {
        toast.error(`Failed to generate ${connectMeetLabelPlural}`);
      }
      setLoadingMessage(<div></div>);
      setLoading(false);
    });
  };

  const getMemberHistory = () => {
    ApiClient.get(`api/member/event/groups?email=${user?.email}`).then(res => {
      if (res.success) {
        setmemberHistoryData(res?.data)
      }
    })
  }


  const attendeeFilter = (meetConfirm: any) => {
    let arr = attendee || []

    if (meetConfirm == 'Pending') {
      arr = attendee.filter((itm: any) => itm.meetConfirm == 'Pending' || !itm.meetConfirm)
    } else {
      arr = attendee.filter((itm: any) => itm.meetConfirm == meetConfirm)
    }
    return [...arr].sort((a: any, b: any) => {
      const nameA = (a?.memberDetails?.fullName || a?.fullName || '').toLowerCase();
      const nameB = (b?.memberDetails?.fullName || b?.fullName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    })
  }

  const endEvent = () => {
    // if (window.confirm("Do you want to End this Event")) {
    //   loader(true)
    //   ApiClient.put('api/event/edit', { id: id, meetingStatus: "completed" }).then(res => {
    //     if (res.success) {
    //       getDetail()
    //     } else {
    //       loader(false)
    //     }
    //   })
    // }
    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to End this Event`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes"
    }).then((result) => {
      if (result.isConfirmed) {
        loader(true)
        ApiClient.put('api/event/edit', { id: id, meetingStatus: "completed" }).then(res => {
          if (res.success) {
            getDetail()
          } else {
            loader(false)
          }
        })
        //   Swal.fire({
        //     icon: "success"
        //   });
      }
    });
  }

  const [resendingRsvp, setResendingRsvp] = useState(false);

  const resendRsvpToPending = () => {
    const pendingCount = attendeeFilter('Pending').length;
    if (pendingCount === 0) {
      toast.info('No pending attendees to resend to');
      return;
    }
    Swal.fire({
      title: "Resend RSVP Emails?",
      text: `This will resend the RSVP email to ${pendingCount} attendee(s) who have not responded yet.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Resend"
    }).then((result) => {
      if (result.isConfirmed) {
        setResendingRsvp(true);
        ApiClient.post('api/attendees/resend-rsvp', {
          eventId: id,
          groupId: data?.groupId?._id
        }).then((res: any) => {
          setResendingRsvp(false);
          if (res.success) {
            toast.success(res.message || `RSVP emails resent to ${res.sent} attendee(s)`);
          } else {
            toast.error(res.message || 'Failed to resend RSVP emails');
          }
        }).catch(() => {
          setResendingRsvp(false);
          toast.error('Failed to resend RSVP emails');
        });
      }
    });
  };

  const deletePremit = (row: any) => {
    let value = false
    if (row?.addedBy === user._id || data?.addedBy?._id === user?._id) value = true
    // Check if user's customerRole is in the groupLeaderRoleIds array (GL + Assistant GL)
    const roleId = getUserRoleInEventGroup();
    if (roleId && environment.groupLeaderRoleIds.includes(roleId)) value = true
    if (roleId && environment.connectmeetmanagerRoleId === roleId) value = true
    if (data?.meetingStatus == 'completed') value = false
    return value
}

const getUserRoleInEventGroup = () => {
  const eventGroupId = data?.groupId?._id || data?.groupId;
  if (!eventGroupId || !user?.groups) return null;
  const group = user.groups.find((g: any) => {
      const gid = g.groupId?._id || g.groupId;
      return gid === eventGroupId && g.status === 'active';
  });
  if (!group) return null;
  return group.customerRole?._id || group.customerRole;
};

// cw: helper function, both gl and assistant gl are groupleaders
const isGroupLeader = () => {
  const roleId = getUserRoleInEventGroup();
  return roleId && environment.groupLeaderRoleIds.includes(roleId);
}

const addPremit = () => {
  // cw: guests never manage an event (Invite Member/Guest, Manage Check-ins, Manage {CM} Requests).
  if (user?.isGuest) return false
  let value = false
  const roleId = getUserRoleInEventGroup();
  if (data?.addedBy?._id === user._id) value = true
  if (roleId && environment.groupLeaderRoleIds.includes(roleId)) value = true
  if (roleId && environment.connectmeetmanagerRoleId === roleId) value = true

  return value
}

// cw: Who can see "Import Attendance from Zoom" — GL/AGL/CM-owner (addPremit) plus CM Manager and
// TY Manager. They see the button with a static advisory that they must be the meeting host on a
// paid Zoom account (we can't verify that without a user:read scope).
const canImportZoom = () => {
  if (addPremit()) return true;
  const roleId = getUserRoleInEventGroup();
  if (!roleId) return false;
  if (environment.connectmeetmanagerRoleId === roleId) return true;
  if (environment.thankYouRoleIds?.includes(roleId)) return true;
  return false;
}

// cw: Parse an uploaded photo-list PDF, then split parsed people into existing
// group members vs guests and pre-populate the Invite Member / Invite Guest popups.
const handlePhotoListFile = async (e: any) => {
  const file = e.target.files?.[0]
  e.target.value = '' // allow re-selecting the same file later
  if (!file) return
  loader(true)
  const res = await ApiClient.importPhotoList({ file })
  const people = res?.success ? (res.data || []) : []
  if (!res?.success) {
    loader(false)
    toast.error(res?.message || 'Could not read the photo list')
    return
  }
  if (!people.length) {
    loader(false)
    toast.warn('No names and emails were found in that file')
    return
  }
  // Fetch this group's members to decide member vs guest
  const groupId = data?.groupId?._id || data?.groupId
  const mRes = await ApiClient.get('api/members/list', { groupId, isDeleted: false })
  loader(false)
  const groupMembers = (mRes?.success && mRes.data) ? mRes.data : []
  const byEmail: any = {}
  groupMembers.forEach((u: any) => { if (u.email) byEmail[u.email.toLowerCase()] = u })

  const membersSeed: any[] = []
  const guestsSeed: any[] = []
  people.forEach((p: any) => {
    const match = byEmail[(p.email || '').toLowerCase()]
    if (match) {
      // memberId + name are resolved inside AddAttendee against its dropdown list by email.
      // Seed with the account's fullName-derived name (members/list lowercases lastName).
      const full = (match.fullName || `${match.firstName || ''} ${match.lastName || ''}`).trim()
      const parts = full.split(/\s+/)
      membersSeed.push({ email: match.email, firstName: parts[0] || match.firstName || '', lastName: parts.slice(1).join(' ') || match.lastName || '' })
    } else {
      guestsSeed.push({ email: p.email, firstName: p.firstName, lastName: p.lastName, fullName: `${p.firstName} ${p.lastName}`.trim() })
    }
  })

  setPhotoMembers(membersSeed)
  setPhotoGuests(guestsSeed)
  setPhotoImportOpen(true)
}

const csvRsvpRef = React.useRef<HTMLInputElement>(null);

const parseCSV = (text: string) => {
  const rows: string[][] = [];
  let row: string[] = [], field = '', inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += c;
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ',') { row.push(field); field = ''; }
      else if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
      else if (c === '\r') { /* skip */ }
      else field += c;
    }
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows;
};

const [csvRsvpOpen, setCsvRsvpOpen] = useState(false);
const [csvRsvpMembers, setCsvRsvpMembers] = useState<any[]>([]);
const [csvRsvpGuests, setCsvRsvpGuests] = useState<any[]>([]);

const handleCsvRsvp = (e: any) => {
  const file = e.target.files?.[0];
  e.target.value = '';
  if (!file) return;
  const reader = new FileReader();
  reader.onload = async (ev: any) => {
    try {
      const rows = parseCSV(String(ev.target?.result || ''));
      if (!rows.length) { toast.error('CSV appears to be empty'); return; }
      const hdrs = rows[0].map((h: string) => (h || '').trim().toLowerCase());
      const HEADER_MAP: Record<string, string> = {
        'first name': 'firstName', 'firstname': 'firstName',
        'last name': 'lastName', 'lastname': 'lastName',
        'email': 'email',
      };
      const idx: Record<string, number> = {};
      hdrs.forEach((h, i) => { if (HEADER_MAP[h] !== undefined) idx[HEADER_MAP[h]] = i; });
      if (idx.email === undefined) { toast.error('CSV must include an "Email" column'); return; }
      const get = (cols: string[], k: string) => (idx[k] !== undefined ? (cols[idx[k]] || '').trim() : '');
      const people: { firstName: string; lastName: string; email: string }[] = [];
      for (let r = 1; r < rows.length; r++) {
        const cols = rows[r];
        const email = get(cols, 'email');
        if (!email) continue;
        people.push({ firstName: get(cols, 'firstName'), lastName: get(cols, 'lastName'), email });
      }
      if (!people.length) { toast.error('No rows with an email were found'); return; }

      const groupId = data?.groupId?._id || data?.groupId;
      loader(true);
      const mRes = await ApiClient.get('api/members/list', { groupId, isDeleted: false });
      loader(false);
      const groupMembers = (mRes?.success && mRes.data) ? mRes.data : [];
      const byEmail: any = {};
      groupMembers.forEach((u: any) => { if (u.email) byEmail[u.email.toLowerCase()] = u; });

      const membersSeed: any[] = [];
      const guestsSeed: any[] = [];
      people.forEach((p) => {
        const match = byEmail[p.email.toLowerCase()];
        if (match) {
          const full = (match.fullName || `${match.firstName || ''} ${match.lastName || ''}`).trim();
          const parts = full.split(/\s+/);
          membersSeed.push({ email: match.email, firstName: parts[0] || match.firstName || '', lastName: parts.slice(1).join(' ') || match.lastName || '' });
        } else {
          guestsSeed.push({ email: p.email, firstName: p.firstName, lastName: p.lastName, fullName: `${p.firstName} ${p.lastName}`.trim() });
        }
      });

      setCsvRsvpMembers(membersSeed);
      setCsvRsvpGuests(guestsSeed);
      setCsvRsvpOpen(true);
    } catch (err) {
      toast.error('Could not read that CSV file');
    }
  };
  reader.readAsText(file);
};

const gl = () => {
  let value = false
  const roleId = getUserRoleInEventGroup();
  if (data?.addedBy?._id === user._id) value = true
  if (roleId && environment.groupLeaderRoleIds.includes(roleId)) value = true
      
      // console.log('addPremit check:', {
      //   isCreator: data?.addedBy?._id === user._id,
      //   role: role,
      //   isInGLArray: environment.groupLeaderRoleIds.includes(user?.customerRole?._id),
      //   finalValue: value
      // });
      
      return value
  }

  const assistantMeetManager = () => {
    let value = false
    const roleId = getUserRoleInEventGroup();
    if (roleId && environment.groupLeaderRoleIds.includes(roleId)) value = true
    if (roleId && environment.connectmeetmanagerRoleId === roleId) value = true

    return value
}

  const goToBreakoutPage = () => {
    encryptId(id).then((enc: string) => history(`/breakoutDetail/${enc}`))
  }

  // cw: Generate Zoom Breakouts — available on ANY event. Mirrors the "Generate ConnectMeets" popup:
  // shows the checked-in count and asks the facilitator to enter the Zoom Meeting ID + group size,
  // then generates Round 1 and opens the breakout page.
  const openGenerateBreakout = () => {
    loader(true)
    ApiClient.get('api/attendees/list', { eventId: id }).then((res: any) => {
      loader(false)
      if (!res?.success) return
      const checkedInCount = (res.data || []).filter((a: any) => a.isPresent === true).length
      Swal.fire({
        title: 'Generate Zoom Breakouts',
        html: `
          <div style="text-align: left; padding: 10px;">
            <p style="margin-bottom: 15px;"><strong>Checked In:</strong> ${checkedInCount} people</p>
            <label style="display:block; font-size:13px; font-weight:600; margin-bottom:4px;">Zoom Meeting ID</label>
            <input id="swal-breakout-meeting" class="swal2-input" style="margin:0 0 12px 0; width:100%;" placeholder="e.g. 123 456 7890" value="${data?.zoomMeetingId || ''}">
            <label style="display:block; font-size:13px; font-weight:600; margin-bottom:4px;">Group Size</label>
            <input id="swal-breakout-size" class="swal2-input" style="margin:0; width:100%;" inputmode="numeric" placeholder="4" value="${data?.breakoutGroupSize || 4}">
            <hr style="margin: 15px 0;">
            <p style="margin-top: 15px;"><strong>Generate breakout groups with ${checkedInCount} checked-in attendees?</strong></p>
          </div>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, Generate Breakouts',
        cancelButtonText: 'No, Manage Check-ins',
        preConfirm: () => {
          const meetingId = (document.getElementById('swal-breakout-meeting') as HTMLInputElement)?.value?.trim()
          const sizeRaw = (document.getElementById('swal-breakout-size') as HTMLInputElement)?.value || ''
          const groupSize = parseInt(sizeRaw.replace(/[^0-9]/g, ''), 10)
          if (!meetingId) { Swal.showValidationMessage('Please enter a Zoom Meeting ID'); return false }
          if (!groupSize || groupSize < 1) { Swal.showValidationMessage('Group size must be at least 1'); return false }
          return { meetingId, groupSize }
        }
      }).then((result: any) => {
        if (result.isConfirmed && result.value) {
          doGenerateBreakout(result.value.meetingId, result.value.groupSize)
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          setRModal(true)
        }
      })
    }).catch(() => loader(false))
  }

  const doGenerateBreakout = (meetingId: string, groupSize: number) => {
    const evId = data?._id || id
    loader(true)
    ApiClient.post('api/event/breakout/generate', { eventId: evId, meetingId, groupSize }).then((res: any) => {
      loader(false)
      const body = (res && res.data && typeof res.data === 'object') ? res.data : res
      if (res?.success) {
        // cw: persist the meeting ID explicitly too, so it reliably attaches to the event and the
        // breakout page shows the Export-to-Zoom button without asking for it again.
        if (meetingId) ApiClient.post('api/event/breakout/link-meeting', { eventId: evId, meetingId }).catch(() => {})
        if (body.warning) toast.warning(body.warning)
        else toast.success('Breakout groups generated')
        goToBreakoutPage()
      } else {
        toast.error(res?.message || res?.error?.message || 'Failed to generate breakouts')
      }
    }).catch((err: any) => {
      loader(false)
      toast.error(err?.response?.data?.message || 'Failed to generate breakouts')
    })
  }

// cw: ONLY TY Tracker + GL + AGL can manage Thank Yous
const canManageThankYous = () => {
    const roleId = getUserRoleInEventGroup();
    if (!roleId) return false;
    if (environment.thankYouRoleIds?.includes(roleId)) return true;
    return false;
}

  const getMember = (groupId = '') => {
    let f = {
      userId: user._id,
      groupId: groupId,
      count: 1,
      page: 1
    }
    ApiClient.get('api/members/list', f).then(res => {
      if (res.success) {
        let data = res.data?.[0]
        setMember(data)
      }
    })
  }

  const deleteItem = (id: any) => {
    // if (window.confirm("Do you want to delete this")) {
    //   loader(true)
    //   ApiClient.delete('api/attendees', { id: id }).then(res => {
    //     if (res.success) {
    //       getAttendee()
    //     }
    //     loader(false)
    //   })
    // }
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes"
    }).then((result) => {
      if (result.isConfirmed) {
        loader(true)
        ApiClient.delete('api/attendees', { id: id }).then(res => {
          if (res.success) {
            getAttendee()
          }
          loader(false)
        })
        //   Swal.fire({
        //     icon: "success"
        //   });
      }
    });
  }

  const markAttendance = () => {
    loader(true)
    ApiClient.post('api/sentEmail', { eventId: id }).then(res => {
      loader(false)
      if (res.success) {
        toast.success(res.message)
        getDetail()
      }
    })
  }

  useEffect(() => {
    if (!id) return; // wait until id is resolved
    getDetail();
    getAttendee();
    getInvites();
    getMemberHistory();
    // getAttendeesGroupPair()
  }, [id]);


  // cw: solely for debugging
  // useEffect(() => {
  //   console.log("User customerRole._id type:", typeof user?.customerRole?._id);
  //   console.log("User customerRole._id value:", user?.customerRole?._id);
  //   console.log("Environment GLs:", environment.groupLeaderRoleIds);
  // }, [user]);

  // useEffect(() => {
  //   console.log("CMM Debug - isGroupGenerated:", data?.isGroupGenerated);
  //   console.log("CMM Debug - isMarkAttendance:", data?.isMarkAttendance);
  //   console.log("CMM Debug - meetingStart():", meetingStart());
  // }, [data]);

  // cw: helper func to format countdown as months/week/day/hr
  const formatCountdown = (totalHours: number): string => {
    const months = Math.floor(totalHours / (24 * 30));
    const weeks = Math.floor((totalHours % (24 * 30)) / (24 * 7));
    const days = Math.floor((totalHours % (24 * 7)) / 24);
    const hours = Math.floor(totalHours % 24);
    
    const parts = [];
    if (months > 0) parts.push(`${months} month${months !== 1 ? 's' : ''}`);
    if (weeks > 0) parts.push(`${weeks} week${weeks !== 1 ? 's' : ''}`);
    if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
    if (hours > 0) parts.push(`${hours} hr${hours !== 1 ? 's' : ''}`);
    
    return parts.slice(0, 2).join(', ') || 'Starting soon';
  };

  // cw: Meeting countdown will be live, no need to refresh
  useEffect(() => {
    const timer = setInterval(() => {
        if (!data?.date) return;
        
        const now = new Date();
        const eventTime = new Date(data.date);
        const diffMs = eventTime.getTime() - now.getTime();
        
        if (diffMs <= 0) {
            setCountdown("Event started");
            clearInterval(timer);
            return;
        }
        
        const totalHours = Math.floor(diffMs / (1000 * 60 * 60));
        const totalMinutes = Math.floor(diffMs / (1000 * 60));
        
        // For events >24 hours away, show only larger units
        if (totalHours >= 24) {
            const days = Math.floor(totalHours / 24);
            const months = Math.floor(days / 30);
            const weeks = Math.floor((days % 30) / 7);
            const remainingDays = days % 7;
            
            let display = "Meeting will start in ";
            if (months > 0) display += `${months} month${months > 1 ? 's' : ''}, `;
            if (weeks > 0) display += `${weeks} week${weeks > 1 ? 's' : ''}, `;
            if (remainingDays > 0) display += `${remainingDays} day${remainingDays > 1 ? 's' : ''}`;
            
            setCountdown(display.replace(/, $/, ''));
        } else if (totalMinutes <= 10) {
            // Show seconds when 10 minutes or less
            const minutes = Math.floor(diffMs / (1000 * 60));
            const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
            setCountdown(`Meeting will start in ${minutes} min ${seconds} sec`);
        } else {
            // Show precise time for <24 hours
            const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
            setCountdown(`Meeting will start in ${totalHours} hours, ${minutes} minutes`);
        }
    }, 1000);
    
    return () => clearInterval(timer);
  }, [data?.date]);

  const fetchRequestStatuses = async () => {
    if (!id) return;
    try {
      const res = await ApiClient.get("api/group-request/list", { eventId: id });
      if (res?.success && res?.data) {
        const statusMap: {[email: string]: string} = {};
        res.data.forEach((req: any) => {
          if (req.requestedBy) {
            statusMap[req.requestedBy] = req.requestType || 'system_generate';
          }
        });
        setAttendeeRequestStatuses(statusMap);
      }
    } catch (err) {
      console.error("Failed to fetch request statuses:", err);
    }
  };

  useEffect(() => {
    if (id) fetchRequestStatuses();
  }, [id]);

  useEffect(() => {
    if (showManagerRequestModal && id) {
      getAttendee();
      fetchRequestStatuses();
    }
  }, [showManagerRequestModal, id]);

  const getRequestStatus = (email: string) => {
    return attendeeRequestStatuses[email] || 'system_generate';
  };

  const requestCheck = () => {
    let value = false
    if (data?.addedBy?._id != user._id && !me) value = true
    return value
  }

  const request = () => {
    if (!member) {
      toast.error("You are not a member of this Group")
      return
    }
    let payload = {
      eventId: id,
      groupId: data?.groupId?._id || '',
      memberId: member._id,
      fullName: user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
      email: user?.email
    }

    loader(true)
    ApiClient.post('api/event/request', payload).then(res => {
      loader(false)
      if (res.success) {
        getDetail()
        toast.success(res.message)
      }
    })
  }

  const acceptReject = (id: any, joinRequest = '') => {
    // if (window.confirm(`Do you want to ${joinRequest == 'accepted' ? 'Accept' : 'Reject'} this request`)) {
    //   loader(true)
    //   ApiClient.put('api/accept-reject/request', { id: id, status: joinRequest }).then(res => {
    //     loader(false)
    //     if (res.success) {
    //       toast.success(`Request ${joinRequest} successfully`)
    //       getAttendee()
    //       getInvites()
    //     }
    //   })
    // }
    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to ${joinRequest == 'accepted' ? 'Accept' : 'Reject'} this request`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes"
    }).then((result) => {
      if (result.isConfirmed) {
        loader(true)
        ApiClient.put('api/accept-reject/request', { id: id, status: joinRequest }).then(res => {
          loader(false)
          if (res.success) {
            toast.success(`Request ${joinRequest} successfully`)
            getAttendee()
            getInvites()
          }
        })
        //   Swal.fire({
        //     icon: "success"
        //   });
      }
    });
  }

  const isButtonVisible = () => {
    const currentTime = new Date();
    const startTime = new Date(datepipeModel.datetime(data?.date));
    const fifteenMinutesBefore = new Date(startTime.getTime() - 15 * 60 * 1000);

    return currentTime >= fifteenMinutesBefore && currentTime <= startTime;
  };

  // const isButtonVisible = () => {
  //   const currentTime = new Date();
  //   const startTime = new Date(datepipeModel.datetime(data?.date));
  //   const fifteenMinutesBefore = new Date(startTime.getTime() - 15 * 60 * 1000);

  //   // Check if current time is before the start time or within 15 minutes before the start time
  //   return currentTime >= fifteenMinutesBefore;
  // };
  
  // cw: replaced entire meetingStart() with "Event started" logic
  const meetingStart = () => {
    if (!data?.date) return false;
    
    const now = new Date();
    const eventTime = new Date(data?.date);
    
    // Meeting has started if current time is past event time
    return now.getTime() >= eventTime.getTime();
  }

  function handlePrint() {
    const imgElement = document.createElement("img");
    imgElement.src = data?.qrCode;

    const printWindow = window.open("", "_blank") as Window;
    if (printWindow) {
      printWindow.document.write(`<html><head><title>Print Image</title></head><body style='margin: 0; text-align: center;'><img src='${data?.qrCode}' style='max-width: 100%; max-height: 100%;'></body></html>`);
      printWindow.document.close();
      printWindow.print();
      printWindow.close();
    } else {
      console.error("Failed to open print window.");
    }
  }


  const handleOpenModal = (e: any) => {
    if (e === "GenerateConnectmeets") {
        // Fetch fresh attendee data before showing popup
        loader(true);
        ApiClient.get("api/attendees/list", { eventId: id }).then(res => {
            loader(false);
            if (res.success) {
                const freshAttendees = res.data;
                const checkedInCount = freshAttendees.filter((itm: any) => itm.isPresent === true).length;
                const rsvpYesCount = freshAttendees.filter((itm: any) => itm.meetConfirm === 'Yes').length;
                
                Swal.fire({
                  title: `Generate ${connectMeetLabelPlural}`,
                    html: `
                        <div style="text-align: left; padding: 10px;">
                            <p style="margin-bottom: 10px;"><strong>RSVP'd Yes:</strong> ${rsvpYesCount} people</p>
                            <p style="margin-bottom: 15px;"><strong>Checked In:</strong> ${checkedInCount} people</p>
                            <hr style="margin: 15px 0;">
                            <p style="margin-top: 15px;"><strong>Generate groups with ${checkedInCount} checked-in attendees?</strong></p>
                        </div>
                    `,
                    icon: "question",
                    showCancelButton: true,
                    confirmButtonColor: "#3085d6",
                    cancelButtonColor: "#6c757d",
                    confirmButtonText: "Yes, Generate Groups",
                    cancelButtonText: "No, Manage Check-ins"
                }).then((result) => {
                    if (result.isConfirmed) {
                        getAttendeesGroupPair();
                    } else if (result.dismiss === Swal.DismissReason.cancel) {
                        setRModal(true);
                    }
                });
            }
        });
    }
    else if (e === "SeeAllGroups") {
        encryptId(id).then(encrypted => {
            history(`/troika-assignments/${encrypted}`);
        });
    }
}

  interface NewDataItem {
    memberDetails?: {
      email?: string;
    };
    // Add other properties if needed
  }


  function formatDateToTimezone(date: any, timeZone: any) {
    return datepipeModel.formatDateToTimezone(date, timeZone)
  }

  const isInvited = () => {
    let arr = attendeeFilter('Pending') || []
    let ext = arr.find((itm: any) => matchesCurrentUser(itm))
    let value = false
    //if (ext && data?.addedBy?.email != user.email) value = true
    if (ext) value = true  // cw: show the Yes No confirm you are attending button for everyone invited, including the event creator
    return value
  }

  const confirmPopup = (p = '') => {
    let payload = {
      eventId: id,
      userId: user._id,
      status: p
    }

    loader(true)
    ApiClient.put('api/confirm/meet', payload).then(res => {
      loader(false)
      if (res.success) {
        toast.success(res.message)
        getAttendee()
        getInvites()
      }
    })
  }

  // cw: my RSVP for this event (Yes/No/Pending), from the attendee list.
  const myRsvp = attendee.find((itm: any) => (itm.memberDetails?.email || itm.email) === user?.email)?.meetConfirm;

  // cw: when my RSVP is Yes, load the calendar event so we can offer .ics / Google Calendar.
  useEffect(() => {
    if (myRsvp !== 'Yes' || !id) { setCal(null); return; }
    ApiClient.get('api/event/ics', { eventId: id }).then((res: any) => {
      if (res.success && res.ics) setCal({ ics: res.ics, gcalUrl: res.gcalUrl, event: res.event });
    }).catch(() => { /* non-fatal */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myRsvp, id]);

  const downloadIcs = () => {
    if (!cal?.ics) return;
    const blob = new Blob([cal.ics], { type: 'text/calendar;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(cal.event?.title || 'event').replace(/[^a-z0-9]+/gi, '_')}.ics`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Login-free QR check-in: point the QR at the public email+code page carrying the event code.
  const qrUrl = `${window.location.origin}/event-checkin?code=${encodeURIComponent(data?.checkinCode || '')}`;

  const currentStep = data?.meetingStatus === 'completed' ? 3 : meetingStart() ? 2 : 1;
  const stepLabels = ['Before the meeting', 'During the meeting', 'After the meeting'];

  useEffect(() => {
    setSelectedStep(currentStep);
  }, [data?.meetingStatus, data?.date]);

  const checkedInCount = attendee.filter((a: any) => a.isPresent === true).length;

  const exportAttendance = () => {
    const rows = attendee
      .filter((a: any) => a.isPresent === true)
      .map((a: any) => {
        const name = a?.memberDetails?.fullName || a?.fullName || 'Guest';
        const email = a?.memberDetails?.email || a?.email || '';
        const time = a?.checkedInAt || a?.updatedAt || '';
        return `"${name.replace(/"/g, '""')}","${email}","${time}"`;
      });
    const csv = ['Name,Email,Checked In At', ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance-${(data?.title || 'event').replace(/[^a-z0-9]+/gi, '_')}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatCheckinTime = (itm: any) => {
    const ts = itm?.checkedInAt || (itm?.isPresent ? itm?.updatedAt : null);
    if (!ts) return null;
    try {
      const d = new Date(ts);
      const tz = user?.timezone || data?.timezone;
      if (tz) {
        return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: datepipeModel.normalizeTz(tz) });
      }
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } catch { return null; }
  };

  const getInitials = (name: string) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return (parts[0][0] || '?').toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };
  const initialColors = ['#EF7A2B', '#2B91EF', '#22C55E', '#8B5CF6', '#EC4899', '#F59E0B', '#06B6D4', '#6366F1'];
  const getInitialColor = (name: string) => {
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) hash = (name || '').charCodeAt(i) + ((hash << 5) - hash);
    return initialColors[Math.abs(hash) % initialColors.length];
  };

  const cmRequestCount = Object.values(attendeeRequestStatuses).filter(s => s !== 'not_interested').length;

  const getPeopleList = () => {
    let list: any[] = [];
    if (peopleTab === 'going') list = attendeeFilter('Yes');
    else if (peopleTab === 'notgoing') list = attendeeFilter('No');
    else if (peopleTab === 'noreply') list = attendeeFilter('Pending');
    else if (peopleTab === 'guests') list = invites;
    if (peopleSearch.trim()) {
      const q = peopleSearch.toLowerCase();
      list = list.filter((itm: any) => {
        const name = (itm?.memberDetails?.fullName || itm?.fullName || '').toLowerCase();
        const email = (itm?.memberDetails?.email || itm?.email || '').toLowerCase();
        return name.includes(q) || email.includes(q);
      });
    }
    return list;
  };

  const tabCounts = {
    going: attendeeFilter('Yes').length,
    notgoing: attendeeFilter('No').length,
    noreply: attendeeFilter('Pending').length,
    guests: invites.length,
  };

  return (
    <>
      <Layout>
        {loading ? <>
          <div className="loader_spin"></div>
          {loadingMessage}
        </> : <>
          {/* Page Header */}
          <div className="mb-6">
            <Link to="/event" style={{ fontSize: 14, fontWeight: 600, color: '#C2410C', textDecoration: 'none' }} className="inline-flex items-center mb-3">
              &larr; All events
            </Link>
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-gray-900 capitalize">{data?.title}</h1>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 text-sm text-gray-600">
                  <span className="flex items-center gap-1.5">
                    <i className="fa fa-calendar text-orange-500"></i>
                    {datepipeModel.formatDateToTimezone(data?.date, user?.timezone || data?.timezone)}
                    {(() => {
                      const userTz = datepipeModel.normalizeTz(user?.timezone);
                      const eventTz = datepipeModel.normalizeTz(data?.timezone);
                      if (!eventTz || !userTz || eventTz === userTz) return null;
                      const tzOption = timezoneModel.list.find((tz:any) => tz.id === (user?.timezone || userTz));
                      const displayName = tzOption?.name || userTz?.replace(/_/g, ' ');
                      return <span className="text-red-500 text-xs ml-1">⚠️ {displayName}</span>;
                    })()}
                  </span>
                  {data?.address && (
                    <span className="flex items-center gap-1.5 capitalize">
                      <IoLocationSharp className="text-orange-500 shrink-0" />
                      <span className="truncate max-w-xs">{data.address.split('\n')[0]}</span>
                    </span>
                  )}
                  <span>
                    Event Type: <span className="text-orange-500 capitalize">{data?.type === "inPerson" ? "In-Person" : data?.type === "virtual" ? "Virtual" : ""}</span>
                  </span>
                  {data?.externalLink && (
                    <a href={data.externalLink} target="_blank" className="flex items-center gap-1 text-blue-600 hover:underline">
                      <MdInsertLink className="text-orange-500" /> Meeting link
                    </a>
                  )}
                </div>
                {(() => {
                  const userTz = datepipeModel.normalizeTz(user?.timezone);
                  const eventTz = datepipeModel.normalizeTz(data?.timezone);
                  if (eventTz && userTz && eventTz !== userTz) {
                    const tzOption = timezoneModel.list.find((tz:any) => tz.id === (data?.timezone || eventTz));
                    const displayName = tzOption?.name || eventTz?.replace(/_/g, ' ');
                    return (
                      <p className="text-xs text-gray-400 mt-1">
                        Originally created for {datepipeModel.formatDateToTimezone(data?.date, data?.timezone)}, {displayName}
                      </p>
                    );
                  }
                  return null;
                })()}
                {data?.description && (
                  <div className="ql-editor text-gray-600 text-sm leading-6 mt-2" style={{ padding: 0, minHeight: 'auto' }} dangerouslySetInnerHTML={{ __html: data?.description }}></div>
                )}
              </div>
              <div className="flex gap-2.5 shrink-0">
                {addPremit() && data?.meetingStatus !== 'completed' && (
                  <button
                    onClick={async () => { const enc = await encryptId(id); navigate(`/event/edit/${enc}`); }}
                    style={{ height: 44, padding: '0 18px', borderRadius: 12, border: '1px solid #D4D4D0', background: '#FFFFFF', fontSize: 14, fontWeight: 600, color: '#18181B' }}
                  >
                    Edit event
                  </button>
                )}
                <button
                  onClick={() => setShowQrModal(true)}
                  style={{ height: 44, padding: '0 18px', borderRadius: 12, border: '1px solid #D4D4D0', background: '#FFFFFF', fontSize: 14, fontWeight: 600, color: '#18181B' }}
                >
                  Show check-in QR
                </button>
              </div>
            </div>
          </div>

          {/* RSVP Invitation Banner */}
          {isInvited() ? (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6 flex items-center justify-between flex-wrap gap-3">
              <p className="font-medium text-gray-800">You're invited! Will you attend?</p>
              <div className="flex gap-2">
                <button onClick={() => confirmPopup('Yes')} className="bg-green-600 px-5 py-2 text-white text-sm rounded-lg hover:bg-green-700">Yes</button>
                <button onClick={() => confirmPopup('No')} className="bg-red-600 px-5 py-2 text-white text-sm rounded-lg hover:bg-red-700">No</button>
              </div>
            </div>
          ) : (() => {
            const myAtt = attendee.find((itm: any) => (itm.memberDetails?.email || itm.email) === user?.email);
            const rsvp = myAtt?.meetConfirm;
            if (rsvp === 'Yes' || rsvp === 'No') {
              return (
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-sm">Your RSVP: <span className={`font-semibold ${rsvp === 'Yes' ? 'text-green-600' : 'text-red-600'}`}>{rsvp}</span></span>
                  {data?.meetingStatus !== 'completed' && (
                    <button onClick={() => confirmPopup(rsvp === 'Yes' ? 'No' : 'Yes')} className={`${rsvp === 'Yes' ? 'bg-red-600' : 'bg-green-600'} px-4 py-1.5 text-white text-xs rounded-lg`}>
                      Change to {rsvp === 'Yes' ? 'No' : 'Yes'}
                    </button>
                  )}
                </div>
              );
            }
            return null;
          })()}

          {/* Calendar Download */}
          {myRsvp === 'Yes' && cal && (
            <div className="mb-6 border border-orange-200 bg-orange-50 rounded-lg p-4 flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="font-semibold text-gray-800 text-sm">Add to your calendar</p>
                {cal.event?.joinLink && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Zoom: <a href={cal.event.joinLink} target="_blank" rel="noreferrer" className="text-blue-600 underline break-all">{cal.event.joinLink}</a>
                  </p>
                )}
              </div>
              <button onClick={downloadIcs} className="bg-orange-500 text-white px-4 py-2 rounded text-sm font-medium hover:bg-orange-600">
                Download .ics
              </button>
            </div>
          )}

          {/* Meeting Status / Countdown */}
          {data?.meetingStatus !== 'completed' && !meetingStart() && countdown && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 text-red-600 text-sm">{countdown}</div>
          )}

          {/* Request to Join (non-attendees) */}
          {requestCheck() && data?.meetingStatus !== "completed" && (
            <div className="mb-4">
              <button className="bg-[#393C3D] py-2.5 px-6 text-white rounded-lg text-sm" disabled={data?.isRequested ? true : false} onClick={request}>
                {data?.joinRequest === "rejected" ? "Rejected" : data?.isRequested ? 'Requested' : 'Request to Join'}
              </button>
            </div>
          )}

          {/* CM/TY Request buttons for attendees */}
          {data?.meetingStatus !== "completed" && attendee.some((item: any) => matchesCurrentUser(item) && (item?.meetConfirm === 'Yes' || item?.isPresent === true)) && (
            <div className="flex flex-wrap gap-2 mb-4">
              <button onClick={() => setRequestGroup(true)} className="flex items-center gap-1.5 border border-gray-300 py-2 px-4 rounded-lg hover:bg-orange-500 hover:text-white text-sm">
                <LiaUserClockSolid className="text-lg" /> {connectMeetLabel} Request
              </button>
              <button onClick={() => setShowSubmitThankYou(true)} className="flex items-center gap-1.5 border border-gray-300 py-2 px-4 rounded-lg hover:bg-green-500 hover:text-white text-sm">
                <FaHandshake className="text-lg" /> {testimonialLabel} Request
              </button>
            </div>
          )}

          {/* Step Progress Tabs */}
          <div className="grid grid-cols-3 gap-2 mb-6 p-1.5 rounded-2xl" style={{ background: '#EDEDE9' }}>
            {[1, 2, 3].map((step, idx) => (
              <button
                key={step}
                onClick={() => setSelectedStep(step)}
                className="text-left px-4 py-2.5 rounded-xl border-none"
                style={selectedStep === step
                  ? { background: '#FFFFFF', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }
                  : { background: 'transparent' }
                }
              >
                <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#52525B' }}>
                  Step {step}{step === currentStep ? ' · Now' : ''}
                </span>
                <p className="text-base font-bold mt-0.5" style={{ color: selectedStep === step ? '#18181B' : '#3F3F46' }}>{stepLabels[idx]}</p>
              </button>
            ))}
          </div>

          {/* Step-dependent Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {selectedStep === 1 && (<>
              {/* Step 1: RSVPs */}
              <div className="bg-white flex flex-col gap-2.5" style={{ border: '1px solid #E4E4E0', borderRadius: 18, padding: 20 }}>
                <span className="text-sm font-semibold" style={{ color: '#52525B' }}>RSVPs</span>
                <p className="text-3xl font-extrabold text-gray-900">{attendeeFilter('Yes').length} <span className="text-base font-semibold" style={{ color: '#52525B' }}>going</span></p>
                <span className="text-sm" style={{ color: '#3F3F46' }}>{attendeeFilter('Pending').length} haven't answered yet</span>
                {addPremit() && attendeeFilter('Pending').length > 0 && data?.meetingStatus !== 'completed' && (
                  <button onClick={resendRsvpToPending} disabled={resendingRsvp} className="mt-auto w-full text-white font-bold disabled:opacity-50" style={{ height: 44, borderRadius: 12, border: 'none', background: '#C2410C', fontSize: 15 }}>
                    {resendingRsvp ? 'Sending...' : `Remind the ${attendeeFilter('Pending').length}`}
                  </button>
                )}
              </div>
              {/* Step 1: Invite People */}
              <div className="bg-white flex flex-col gap-2.5" style={{ border: '1px solid #E4E4E0', borderRadius: 18, padding: 20 }}>
                <span className="text-sm font-semibold" style={{ color: '#52525B' }}>Invite people</span>
                <span className="text-[15px] leading-relaxed" style={{ color: '#3F3F46' }}>Add a member or guest, or upload a list from your last meeting.</span>
                {addPremit() && data?.meetingStatus !== 'completed' ? (
                  <div className="mt-auto flex flex-wrap gap-2">
                    <button onClick={() => setModal('member')} style={{ height: 44, padding: '0 14px', borderRadius: 12, border: '1px solid #D4D4D0', background: '#FFFFFF', fontSize: 14, fontWeight: 600, color: '#18181B' }}>Invite member</button>
                    <button onClick={() => setModal('guest')} style={{ height: 44, padding: '0 14px', borderRadius: 12, border: '1px solid #D4D4D0', background: '#FFFFFF', fontSize: 14, fontWeight: 600, color: '#18181B' }}>Invite guest</button>
                    <button onClick={() => csvRsvpRef.current?.click()} style={{ height: 44, padding: '0 14px', borderRadius: 12, border: '1px solid #D4D4D0', background: '#FFFFFF', fontSize: 14, fontWeight: 600, color: '#18181B' }}>Upload CSV</button>
                    <input ref={csvRsvpRef} type="file" accept=".csv" className="hidden" onChange={handleCsvRsvp} />
                  </div>
                ) : (
                  <p className="text-sm" style={{ color: '#3F3F46' }}>{data?.meetingStatus === 'completed' ? 'Event has ended' : 'Contact your group leader to invite people'}</p>
                )}
              </div>
              {/* Step 1: CM Requests */}
              <div className="bg-white flex flex-col gap-2.5" style={{ border: '1px solid #E4E4E0', borderRadius: 18, padding: 20 }}>
                <span className="text-sm font-semibold" style={{ color: '#52525B' }}>{connectMeetLabel} requests</span>
                <p className="text-3xl font-extrabold text-gray-900">{cmRequestCount}</p>
                <span className="text-sm" style={{ color: '#3F3F46' }}>From {attendee.filter((a: any) => a.meetConfirm === 'Yes' || a.isPresent).length} members, collected until the meeting starts</span>
                {addPremit() && (
                  <button onClick={() => setShowManagerRequestModal(true)} className="mt-auto w-full" style={{ height: 44, borderRadius: 12, border: '1px solid #D4D4D0', background: '#FFFFFF', fontSize: 14, fontWeight: 600, color: '#18181B' }}>
                    Review requests
                  </button>
                )}
              </div>
            </>)}

            {selectedStep === 2 && (<>
              {/* Step 2: Checked in (GL/AGL/CMM only) */}
              {addPremit() && (
                <div className="bg-white flex flex-col gap-2.5" style={{ border: '1px solid #E4E4E0', borderRadius: 18, padding: 20 }}>
                  <span className="text-sm font-semibold" style={{ color: '#52525B' }}>Checked in</span>
                  <p className="text-3xl font-extrabold text-gray-900">{checkedInCount} <span className="text-base font-semibold" style={{ color: '#52525B' }}>here now</span></p>
                  <span className="text-sm" style={{ color: '#3F3F46' }}>
                    {attendee.filter((a: any) => a.meetConfirm === 'Yes' && a.isPresent).length} of {attendee.filter((a: any) => a.meetConfirm === 'Yes').length} who said yes{attendee.filter((a: any) => a.isPresent && a.meetConfirm !== 'Yes').length > 0 ? `, plus ${attendee.filter((a: any) => a.isPresent && a.meetConfirm !== 'Yes').length} walk-ins` : ''}
                  </span>
                  <button onClick={() => setRModal(true)} className="mt-auto w-full" style={{ height: 44, borderRadius: 12, border: '1px solid #D4D4D0', background: '#FFFFFF', fontSize: 14, fontWeight: 600, color: '#18181B' }}>
                    Manage check-ins
                  </button>
                </div>
              )}
              {/* Step 2: Assign / View troikas */}
              {addPremit() ? (
                <div className="flex flex-col gap-2.5" style={{ border: '1px solid #FBC9A6', borderRadius: 18, padding: 20, background: '#FFF7F1' }}>
                  <span className="text-sm font-bold" style={{ color: '#9A3412' }}>Next step</span>
                  <span className="text-[15px] font-bold" style={{ color: '#18181B' }}>Assign {connectMeetLabel.toLowerCase()}s before everyone leaves</span>
                  <span className="text-[15px] leading-relaxed" style={{ color: '#3F3F46' }}>Groups of 3 for follow-up meetings, honoring requests where possible. Review and adjust, then share so people can schedule while they're still in the room.</span>
                  {data?.isGroupGenerated ? (
                    <button onClick={() => handleOpenModal("SeeAllGroups")} className="mt-auto w-full text-white font-bold" style={{ height: 48, borderRadius: 12, border: 'none', background: '#C2410C', fontSize: 15 }}>
                      View {connectMeetLabel.toLowerCase()} assignments
                    </button>
                  ) : (
                    <button onClick={() => handleOpenModal("GenerateConnectmeets")} className="mt-auto w-full text-white font-bold" style={{ height: 48, borderRadius: 12, border: 'none', background: '#C2410C', fontSize: 15 }}>
                      Assign {connectMeetLabel.toLowerCase()}s
                    </button>
                  )}
                </div>
              ) : (
                <div className="bg-white flex flex-col gap-2.5" style={{ border: '1px solid #E4E4E0', borderRadius: 18, padding: 20 }}>
                  <span className="text-sm font-semibold" style={{ color: '#52525B' }}>{connectMeetLabel} assignments</span>
                  {data?.isGroupGenerated ? (
                    <>
                      <span className="text-[15px] leading-relaxed" style={{ color: '#3F3F46' }}>Your follow-up groups have been created. See who you're meeting with.</span>
                      <button onClick={() => handleOpenModal("SeeAllGroups")} className="mt-auto w-full" style={{ height: 44, borderRadius: 12, border: '1px solid #D4D4D0', background: '#FFFFFF', fontSize: 14, fontWeight: 600, color: '#18181B' }}>
                        View {connectMeetLabel.toLowerCase()} assignments
                      </button>
                    </>
                  ) : (
                    <span className="text-[15px] leading-relaxed" style={{ color: '#3F3F46' }}>Follow-up groups haven't been assigned yet. Your group leader will share them soon.</span>
                  )}
                </div>
              )}
              {/* Step 2: Virtual attendees (GL/AGL/CMM only) */}
              {addPremit() && (
                <div className="bg-white flex flex-col gap-2.5" style={{ border: '1px solid #E4E4E0', borderRadius: 18, padding: 20 }}>
                  <span className="text-sm font-semibold" style={{ color: '#52525B' }}>Virtual attendees</span>
                  <span className="text-[15px] leading-relaxed" style={{ color: '#3F3F46' }}>Pull in people who joined on Zoom so they're counted and grouped too.</span>
                  <button onClick={openZoomImport} className="mt-auto w-full" style={{ height: 44, borderRadius: 12, border: '1px solid #D4D4D0', background: '#FFFFFF', fontSize: 14, fontWeight: 600, color: '#18181B' }}>
                    Import from Zoom
                  </button>
                </div>
              )}
            </>)}

            {selectedStep === 3 && (<>
              {/* Step 3: Gratitudes / Thank Yous */}
              <div className="bg-white flex flex-col gap-2.5" style={{ border: '1px solid #E4E4E0', borderRadius: 18, padding: 20 }}>
                <span className="text-sm font-semibold" style={{ color: '#52525B' }}>{testimonialLabelPlural}</span>
                <p className="text-3xl font-extrabold text-gray-900">{attendee.filter((a: any) => a.meetConfirm === 'Yes' || a.isPresent).length}</p>
                <span className="text-sm" style={{ color: '#3F3F46' }}>Given during this meeting</span>
                {(addPremit() || canManageThankYous()) && (
                  <button onClick={() => setShowThankYouModal(true)} className="mt-auto w-full" style={{ height: 44, borderRadius: 12, border: '1px solid #D4D4D0', background: '#FFFFFF', fontSize: 14, fontWeight: 600, color: '#18181B' }}>
                    Review {testimonialLabelPlural.toLowerCase()}
                  </button>
                )}
              </div>
              {/* Step 3: Attendance Record */}
              <div className="bg-white flex flex-col gap-2.5" style={{ border: '1px solid #E4E4E0', borderRadius: 18, padding: 20 }}>
                <span className="text-sm font-semibold" style={{ color: '#52525B' }}>Attendance record</span>
                <span className="text-[15px] leading-relaxed" style={{ color: '#3F3F46' }}>{checkedInCount} attended. {connectMeetLabel} assignments are saved to history so people aren't paired again too soon.</span>
                <button onClick={exportAttendance} className="mt-auto w-full" style={{ height: 44, borderRadius: 12, border: '1px solid #D4D4D0', background: '#FFFFFF', fontSize: 14, fontWeight: 600, color: '#18181B' }}>
                  Export attendance
                </button>
              </div>
              {/* Step 3: Wrap Up */}
              <div className="flex flex-col gap-2.5" style={{ border: '1px solid #FBC9A6', borderRadius: 18, padding: 20, background: '#FFF7F1' }}>
                <span className="text-sm font-bold" style={{ color: '#9A3412' }}>Wrap up</span>
                <span className="text-[15px] leading-relaxed" style={{ color: '#3F3F46' }}>Closing the meeting stops check-ins and sends the follow-up email.</span>
                {(gl() || assistantMeetManager()) && data?.meetingStatus !== 'completed' && (
                  <button onClick={endEvent} className="mt-auto w-full text-white font-bold" style={{ height: 48, borderRadius: 12, border: 'none', background: '#C2410C', fontSize: 15 }}>
                    End meeting
                  </button>
                )}
                {data?.meetingStatus === 'completed' && (
                  <div className="mt-auto flex items-center gap-2 text-sm" style={{ color: '#52525B' }}><RiRestTimeLine /> Meeting ended</div>
                )}
              </div>
            </>)}
          </div>

          {/* People Section */}
          <div className="bg-white overflow-hidden mb-6" style={{ border: '1px solid #E4E4E0', borderRadius: 18 }}>
            <div className="flex flex-wrap items-center justify-between gap-3" style={{ padding: '18px 20px 0' }}>
              <h2 className="m-0 font-bold" style={{ fontSize: 18 }}>People</h2>
              <label className="flex items-center gap-2" style={{ height: 40, padding: '0 12px', borderRadius: 10, border: '1px solid #D4D4D0', fontSize: 14, color: '#52525B' }}>
                <i className="fa fa-search" style={{ fontSize: 14, color: '#52525B' }}></i>
                <input
                  type="search"
                  placeholder="Search by name or email"
                  value={peopleSearch}
                  onChange={e => setPeopleSearch(e.target.value)}
                  style={{ border: 'none', outline: 'none', fontSize: 14, width: 200, background: 'transparent' }}
                />
              </label>
            </div>
            {/* Tabs */}
            <div className="flex flex-wrap gap-1 overflow-x-auto" style={{ padding: '12px 20px 0', borderBottom: '1px solid #EFEFEC' }}>
              {([
                { key: 'going', label: 'Going' },
                { key: 'notgoing', label: 'Not going' },
                { key: 'noreply', label: 'No reply' },
                { key: 'guests', label: 'Invited guests' },
              ] as { key: keyof typeof tabCounts; label: string }[]).map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setPeopleTab(tab.key)}
                  style={{
                    height: 44, padding: '0 14px', border: 'none',
                    borderBottom: peopleTab === tab.key ? '2px solid #C2410C' : '2px solid transparent',
                    background: 'transparent', fontSize: 14,
                    fontWeight: peopleTab === tab.key ? 700 : 600,
                    color: peopleTab === tab.key ? '#18181B' : '#3F3F46',
                    cursor: 'pointer', whiteSpace: 'nowrap'
                  }}
                >
                  {tab.label} <span style={{ fontWeight: 500, color: '#52525B' }}>{tabCounts[tab.key]}</span>
                </button>
              ))}
            </div>
            {/* People List */}
            {aloading ? (
              <div style={{ padding: '16px 20px' }}>
                <div className="shine mb-3"></div>
                <div className="shine mb-3"></div>
                <div className="shine mb-3"></div>
              </div>
            ) : (
              <>
                <div className="flex flex-col">
                  {getPeopleList().slice(0, 5).map((itm: any, idx: number) => {
                    const name = itm?.memberDetails?.fullName || itm?.fullName || 'Guest';
                    const email = itm?.memberDetails?.email || itm?.email || '';
                    const uid = itm?.userId || itm?.memberDetails?._id;
                    const isCheckedIn = itm?.isPresent === true;
                    const checkinTime = formatCheckinTime(itm);
                    return (
                      <div key={itm.id || itm._id || idx} className="flex flex-wrap items-center" style={{ gap: 14, padding: '12px 20px', borderBottom: '1px solid #F3F3F0' }}>
                        <span className="flex items-center justify-center shrink-0" style={{ width: 36, height: 36, borderRadius: 999, background: '#F4F4F5', color: '#3F3F46', fontSize: 13, fontWeight: 700 }}>
                          {getInitials(name)}
                        </span>
                        <div className="flex-1 flex flex-col" style={{ minWidth: 200 }}>
                          {uid ? (
                            <Link to={`/user-profile/${uid}?groupId=${data?.groupId?._id || data?.groupId}`} className="capitalize hover:underline" style={{ fontSize: 15, fontWeight: 600, color: '#18181B', textDecoration: 'none' }}>{name}</Link>
                          ) : (
                            <span className="capitalize" style={{ fontSize: 15, fontWeight: 600 }}>{name}</span>
                          )}
                          <span style={{ fontSize: 13, color: '#52525B' }}>{email}</span>
                        </div>
                        {peopleTab === 'guests' && addPremit() ? (
                          <div className="flex gap-1 shrink-0">
                            <button onClick={() => acceptReject(itm.id || itm._id, 'accepted')} className="text-green-600 hover:text-green-700" title="Accept">
                              <span className="material-symbols-outlined text-base">done</span>
                            </button>
                            <button onClick={() => acceptReject(itm.id || itm._id, 'rejected')} className="text-red-600 hover:text-red-700" title="Reject">
                              <span className="material-symbols-outlined text-base">close</span>
                            </button>
                          </div>
                        ) : peopleTab !== 'guests' && (
                          <span className="shrink-0" style={{ fontSize: 13, color: '#52525B', minWidth: 120 }}>
                            {isCheckedIn ? (checkinTime ? `Checked in ${checkinTime}` : 'Checked in') : 'Not here yet'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {getPeopleList().length === 0 && (
                    <div className="text-center py-8" style={{ fontSize: 14, color: '#52525B' }}>No people in this category</div>
                  )}
                </div>
                <div className="flex items-center justify-between" style={{ padding: '14px 20px', fontSize: 14, color: '#52525B' }}>
                  <span>Showing {Math.min(5, getPeopleList().length)} of {getPeopleList().length}</span>
                  {getPeopleList().length > 5 && (
                    <a onClick={() => setRModal(true)} style={{ fontSize: 14, fontWeight: 600, color: '#C2410C', cursor: 'pointer', textDecoration: 'none' }}>See all</a>
                  )}
                </div>
              </>
            )}
          </div>

          {/* More Tools (collapsed) */}
          <div className="border rounded-lg overflow-hidden mb-6 bg-white">
            <button onClick={() => setMoreToolsOpen(!moreToolsOpen)} className="w-full px-4 py-3 flex justify-between items-center text-sm font-medium text-gray-700 hover:bg-gray-50">
              <span>More tools</span>
              <i className={`fa ${moreToolsOpen ? 'fa-chevron-up' : 'fa-chevron-down'} text-gray-400`}></i>
            </button>
            {moreToolsOpen && (
              <div className="px-4 pb-4 flex flex-wrap gap-2 border-t border-gray-100 pt-3">
                {addPremit() && data?.groupId?.photoList && (
                  <>
                    <button onClick={() => photoFileRef.current?.click()} className="bg-[#393C3D] text-white py-2 px-4 rounded-lg text-sm hover:bg-gray-700">Import photo list</button>
                    <input ref={photoFileRef} type="file" accept="application/pdf" className="hidden" onChange={handlePhotoListFile} />
                  </>
                )}
                {canImportZoom() && data?.type === 'virtual' && data?.meetingStatus !== 'completed' && (
                  <button onClick={openZoomImport} className="bg-[#2D8CFF] text-white py-2 px-4 rounded-lg text-sm hover:bg-blue-600">Import Attendance from Zoom</button>
                )}
                {addPremit() && data?.meetingStatus !== 'completed' && (
                  <button onClick={() => setRModal(true)} className="bg-[#0E9F6E] text-white py-2 px-4 rounded-lg text-sm hover:bg-green-700">Manage Check-ins ({attendee.filter((a: any) => a.isPresent === true).length})</button>
                )}
                {addPremit() && (
                  <button onClick={() => setRsvpStatusModal(true)} className="bg-[#2B91EF] text-white py-2 px-4 rounded-lg text-sm hover:bg-blue-600">Check RSVP Status</button>
                )}
                {canManageThankYous() && (
                  <button onClick={() => setShowThankYouModal(true)} className="bg-[#22C55E] text-white py-2 px-4 rounded-lg text-sm hover:bg-green-600">Manage {testimonialLabelPlural}</button>
                )}
                {attendeeFilter('Yes').length > 0 && (
                  <button onClick={() => { if (id) encryptId(id).then((enc: string) => history(`/registration-list/${enc}`)); }} className="border border-gray-300 text-gray-700 py-2 px-4 rounded-lg text-sm hover:bg-gray-50">
                    "Who's coming" photo list
                  </button>
                )}
                {data?.meetingStatus === 'completed' && canManageThankYous() && data?.updatedAt && (Date.now() - new Date(data.updatedAt).getTime() < 24 * 60 * 60 * 1000) && (
                  <button onClick={() => setShowThankYouModal(true)} className="bg-[#22C55E] text-white py-2 px-4 rounded-lg text-sm hover:bg-green-600">Manage {testimonialLabelPlural}</button>
                )}
              </div>
            )}
          </div>


          {/* Host Info */}
          {data?.groupLeaderDetail?.fullName && (
            <div className="border rounded-lg p-5 bg-white mb-6">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Event Host</h3>
              <div className="flex items-center gap-4">
                <div className="border-4 border-orange-500 rounded-full">
                  <img className="h-14 w-14 rounded-full object-cover" src="/assets/img/event-banner.png" alt="" />
                </div>
                <div>
                  <h6 className="text-gray-800 font-bold">{data?.groupLeaderDetail?.fullName}</h6>
                  <span className="text-orange-500 text-sm capitalize">
                    {environment.groupLeaderRoleIds.includes(data?.addedBy?.customerRole?._id) ? "Group Leader" : data?.addedBy?.customerRole?.name || "Event Host"}
                  </span>
                </div>
              </div>
            </div>
          )}
        </>}
      </Layout>

      {showQrModal ? <>
        <Modal
          title="Check-in QR Code"
          result={() => setShowQrModal(false)}
          body={<>
            <div className="flex flex-col items-center gap-4 py-2">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUrl)}`}
                alt="Check-in QR Code"
                className="border-2 border-gray-300 rounded"
              />
              <p className="font-semibold text-gray-800">Scan to check in</p>
              {data?.checkinCode && (
                <a href={qrUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline text-sm">Open check-in link</a>
              )}
              <div className="flex gap-2 mt-2">
                <button className="bg-[#EF7A2B] text-white py-2 px-4 rounded-lg text-sm flex items-center gap-2 hover:bg-orange-600" onClick={handlePrint}>
                  <FiPrinter /> Print QR
                </button>
              </div>
            </div>
          </>}
        />
      </> : <></>}

      {zoomImportOpen ? <>
        <Modal
          title="Import Attendance from Zoom"
          result={() => setZoomImportOpen(false)}
          body={<>
            {zoomBusy ? <div className="py-6 text-center text-gray-500">Loading…</div> : null}
            {!zoomBusy && !zoomSummary ? <>
              <p className="text-sm text-gray-600 mb-3">Enter the Zoom Meeting ID for this event. The app reads its participant list (names &amp; emails) and marks matching attendees present. No Zoom meeting is created or modified.</p>
              <p className="text-xs text-gray-500 mb-3">Find your Meeting ID in the Zoom web portal under Meetings &gt; Previous Meetings, or in the meeting invite.</p>
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
                You must be the meeting host on a paid Zoom account for attendance data to be available. You can re-run this anytime as people join or leave — attendance updates each time.
              </div>
              <input
                type="text"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm mb-3"
                placeholder="e.g. 123 456 7890"
                value={zoomMeetingIdInput}
                onChange={e => setZoomMeetingIdInput(e.target.value.replace(/\s/g, ''))}
              />
              <button
                className="bg-[#2D8CFF] text-white text-sm px-4 py-2 rounded disabled:opacity-50"
                disabled={!zoomMeetingIdInput.trim()}
                onClick={() => runZoomImport(zoomMeetingIdInput.trim())}
              >Import Attendance</button>
            </> : null}
            {zoomSummary ? <>
              <div className="py-2">
                <p className="text-green-700 font-medium mb-2">Marked {zoomSummary.markedCount} attendee(s) present from {zoomSummary.totalParticipants} Zoom participant(s).</p>
                {zoomSummary.matched?.length ? <>
                  <p className="text-sm text-gray-700 font-medium mb-1">Marked present ({zoomSummary.matched.length}):</p>
                  <ul className="text-xs text-gray-600 max-h-40 overflow-y-auto list-disc list-inside mb-3">
                    {zoomSummary.matched.map((name: string, i: number) => <li key={i}>{name}</li>)}
                  </ul>
                </> : null}
                {zoomAddGuests.length > 0 ? <>
                  <p className="text-sm text-gray-700 font-medium mb-1">Not on any list ({zoomAddGuests.length})</p>
                  <p className="text-xs text-gray-500 mb-2">These Zoom participants weren't matched to a member, RSVP, or guest. Edit a name/email, remove anyone with ✕, then <strong>Save</strong> to add the rest as guests (marked present).</p>
                  <div className="max-h-56 overflow-y-auto mb-3 space-y-1">
                    {zoomAddGuests.map((g: any, i: number) => (
                      <div key={i} className="flex items-center gap-2">
                        <input
                          className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm min-w-0"
                          placeholder="Name"
                          value={g.name || ''}
                          onChange={e => updateZoomGuest(i, 'name', e.target.value)}
                        />
                        <input
                          className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm min-w-0"
                          placeholder="email@example.com"
                          value={g.email || ''}
                          onChange={e => updateZoomGuest(i, 'email', e.target.value)}
                        />
                        <button className="text-red-500 hover:text-red-700 flex-shrink-0 px-1" onClick={() => removeZoomGuest(i)} title="Don't record this participant">✕</button>
                      </div>
                    ))}
                  </div>
                </> : null}
                <button className="mt-1 bg-orange-500 hover:bg-orange-600 text-white text-sm px-4 py-2 rounded disabled:opacity-50" onClick={saveZoomGuests} disabled={zoomBusy}>Save</button>
              </div>
            </> : null}
          </>}
        />
      </> : <></>}

      {isModal ? <>
        <Modal
          title={`Invite ${isModal}`}
          result={e => {
            setModal('')
          }}
          onCloseConfirm={async () => {
            const dirtyRef = isModal === 'guest' ? guestFormDirtyRef : memberFormDirtyRef;
            if (!dirtyRef.current) return true;
            const result = await Swal.fire({
              title: "Save Event Invitation",
              text: "You have unsaved changes. Do you want to discard them?",
              icon: "warning",
              showCancelButton: true,
              confirmButtonColor: "#d33",
              cancelButtonColor: "#6c757d",
              confirmButtonText: "Discard",
              cancelButtonText: "Continue Editing"
            });
            return result.isConfirmed;
          }}
          body={<>
            <AddAttendee
              eventDetail={data}
              eventId={id}
              guest={isModal == 'guest' ? true : false}
              result={e => {
                if (e.event == 'submit') {
                  setModal('')
                  getAttendee()
                  getInvites()
                }
              }}
              onDirtyChange={(dirty: boolean) => {
                if (isModal === 'guest') guestFormDirtyRef.current = dirty;
                else memberFormDirtyRef.current = dirty;
              }}
            />
          </>}
        />
      </> : <></>}

      {photoImportOpen ? <>
        <Modal
          title="Import Photo List"
          className="max-w-6xl"
          result={() => setPhotoImportOpen(false)}
          body={<>
            <p className="text-sm text-gray-600 mb-3">Review the entries below and click Send Invite on each side. Nothing is sent until you do. Close with the X to discard.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {photoMembers.length ? <div>
                <h4 className="font-semibold text-gray-800 mb-2">Invite Members ({photoMembers.length})</h4>
                <AddAttendee
                  eventDetail={data}
                  eventId={id}
                  guest={false}
                  initialMembers={photoMembers as any}
                  result={(e: any) => {
                    if (e.event == 'submit') {
                      getAttendee()
                      getInvites()
                      setPhotoMembers([])
                      if (!photoGuests.length) setPhotoImportOpen(false)
                    }
                  }}
                />
              </div> : null}
              {photoGuests.length ? <div>
                <h4 className="font-semibold text-gray-800 mb-2">Invite Guests ({photoGuests.length})</h4>
                <AddAttendee
                  eventDetail={data}
                  eventId={id}
                  guest={true}
                  initialMembers={photoGuests as any}
                  result={(e: any) => {
                    if (e.event == 'submit') {
                      getAttendee()
                      getInvites()
                      setPhotoGuests([])
                      if (!photoMembers.length) setPhotoImportOpen(false)
                    }
                  }}
                />
              </div> : null}
            </div>
          </>}
        />
      </> : <></>}

      {csvRsvpOpen ? <>
        <Modal
          title="RSVP from CSV"
          className="max-w-6xl"
          result={() => { setCsvRsvpOpen(false); setCsvRsvpMembers([]); setCsvRsvpGuests([]); }}
          body={<>
            <p className="text-sm text-gray-600 mb-3">Review the entries below and click Send Invite on each side. Nothing is sent until you do. Close with the X to discard.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {csvRsvpMembers.length ? <div>
                <h4 className="font-semibold text-gray-800 mb-2">Invite Members ({csvRsvpMembers.length})</h4>
                <AddAttendee
                  eventDetail={data}
                  eventId={id}
                  guest={false}
                  initialMembers={csvRsvpMembers as any}
                  result={(e: any) => {
                    if (e.event == 'submit') {
                      getAttendee()
                      getInvites()
                      setCsvRsvpMembers([])
                      if (!csvRsvpGuests.length) setCsvRsvpOpen(false)
                    }
                  }}
                />
              </div> : null}
              {csvRsvpGuests.length ? <div>
                <h4 className="font-semibold text-gray-800 mb-2">Invite Guests ({csvRsvpGuests.length})</h4>
                <AddAttendee
                  eventDetail={data}
                  eventId={id}
                  guest={true}
                  initialMembers={csvRsvpGuests as any}
                  result={(e: any) => {
                    if (e.event == 'submit') {
                      getAttendee()
                      getInvites()
                      setCsvRsvpGuests([])
                      if (!csvRsvpMembers.length) setCsvRsvpOpen(false)
                    }
                  }}
                />
              </div> : null}
            </div>
          </>}
        />
      </> : <></>}

      {isRModal ? <>
        <Modal
          title={`Check-in List (${attendee.filter((a: any) => a.isPresent === true).length})`}


          body={<>
            <Members
              eventId={id}
              eventDetail={data}
              getDetailApi={getDetail}
            />
          </>}
          result={e => {
            setRModal(false)
          }}
        />
      </> : <></>}


      {/* {group ? <>
        <Modal
          
          title="Group List"
          body={<>
         <Groups
         eventDetail={attendeesGroup}
         dataa={data}
         />
          </>}
          result={e => {
            setGroup(false)
          }}
        />
      </> : <></>} */}
      {memberHistory ? <>
        <Modal

          title="Member History"
          body={<>
            <MemberHistory eventDetail={memberHistoryData} />
          </>}
          result={e => {
            setmemberHistory(false)
          }}
        />
      </> : <></>}

      {requestGroup ? <>
        <Modal

          title={`${connectMeetLabel} Request`}
          body={<>
            <RequestGroup eventDetail={attendeeFilter} data={data} setRequestGroup={setRequestGroup} connectMeetLabel={connectMeetLabel} />
          </>}
          result={e => {
            setRequestGroup(false)
          }}
        />
      </> : <></>}
      {isRsvpStatusModal ? <>
        <Modal
          title="RSVP Status"
          body={<>
            <div className="max-h-96 overflow-y-auto">
              <div className="mb-3 p-3 bg-gray-50 rounded">
                <div className="flex justify-between text-sm">
                  <span>Total Invited: <strong>{attendee.length}</strong></span>
                  <span className="text-green-600">RSVP Yes: <strong>{attendee.filter((a: any) => a.meetConfirm === 'Yes').length}</strong></span>
                  <span className="text-red-600">RSVP No: <strong>{attendee.filter((a: any) => a.meetConfirm === 'No').length}</strong></span>
                  <span className="text-yellow-600">Pending: <strong>{attendee.filter((a: any) => !a.meetConfirm || a.meetConfirm === 'Pending').length}</strong></span>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Name</th>
                    <th className="text-left p-2">Email</th>
                    <th className="text-center p-2">RSVP</th>
                    <th className="text-center p-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {[...attendee].sort((a: any, b: any) => {
                    const nameA = (a?.memberDetails?.fullName || a?.fullName || '').toLowerCase();
                    const nameB = (b?.memberDetails?.fullName || b?.fullName || '').toLowerCase();
                    return nameA.localeCompare(nameB);
                  }).map((itm: any, index: number) => (
                    <tr key={index} className="border-b">
                      <td className="p-2">{itm?.memberDetails?.fullName || itm?.fullName || 'Guest'}</td>
                      <td className="p-2">{itm?.email || itm?.memberDetails?.email}</td>
                      <td className="p-2 text-center">
                        <span className={`px-2 py-1 rounded text-xs ${
                          itm?.meetConfirm === 'Yes' ? 'bg-green-100 text-green-700' :
                          itm?.meetConfirm === 'No' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {itm?.meetConfirm || 'Pending'}
                        </span>
                      </td>
                      <td className="p-2 text-center">
                        <div className="flex justify-center gap-1">
                          <button 
                            onClick={() => {
                              loader(true);
                              ApiClient.put('api/attendees/update', { id: itm.id || itm._id, meetConfirm: 'Yes' }).then(res => {
                                loader(false);
                                if (res.success) {
                                  getAttendee();
                                }
                              });
                            }}
                            className={`px-2 py-1 rounded text-xs ${itm?.meetConfirm === 'Yes' ? 'bg-green-600 text-white' : 'bg-gray-200 hover:bg-green-500 hover:text-white'}`}
                          >
                            Yes
                          </button>
                          <button 
                            onClick={() => {
                              loader(true);
                              ApiClient.put('api/attendees/update', { id: itm.id || itm._id, meetConfirm: 'No' }).then(res => {
                                loader(false);
                                if (res.success) {
                                  getAttendee();
                                }
                              });
                            }}
                            className={`px-2 py-1 rounded text-xs ${itm?.meetConfirm === 'No' ? 'bg-red-600 text-white' : 'bg-gray-200 hover:bg-red-500 hover:text-white'}`}
                          >
                            No
                          </button>
                          <button 
                            onClick={() => {
                              loader(true);
                              ApiClient.put('api/attendees/update', { id: itm.id || itm._id, meetConfirm: 'Pending' }).then(res => {
                                loader(false);
                                if (res.success) {
                                  getAttendee();
                                }
                              });
                            }}
                            className={`px-2 py-1 rounded text-xs ${!itm?.meetConfirm || itm?.meetConfirm === 'Pending' ? 'bg-yellow-500 text-white' : 'bg-gray-200 hover:bg-yellow-400 hover:text-white'}`}
                          >
                            Pending
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {attendee.length === 0 && (
                <div className="text-center py-4 text-gray-500">No attendees yet</div>
              )}
              <div className="mt-4 p-3 bg-gray-50 rounded">
                <div className="flex justify-between text-sm">
                  <span>Total Invited: <strong>{attendee.length}</strong></span>
                  <span className="text-green-600">RSVP Yes: <strong>{attendee.filter((a: any) => a.meetConfirm === 'Yes').length}</strong></span>
                  <span className="text-red-600">RSVP No: <strong>{attendee.filter((a: any) => a.meetConfirm === 'No').length}</strong></span>
                  <span className="text-yellow-600">Pending: <strong>{attendee.filter((a: any) => !a.meetConfirm || a.meetConfirm === 'Pending').length}</strong></span>
                </div>
              </div>
            </div>
          </>}
          result={e => {
            setRsvpStatusModal(false)
          }}
        />
      </> : <></>}
      {showManagerRequestModal ? <>
        <Modal
          title={`Manage ${connectMeetLabel} Requests`}
          body={<>
            <div className="max-h-96 overflow-y-auto">
              <p className="mb-4 text-sm text-gray-600">Select an attendee to make a {connectMeetLabel} request on their behalf:</p>
              <table className="w-full text-sm">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Name</th>
                    <th className="text-left p-2">Email</th>
                    <th className="text-center p-2">Current Status</th>
                    <th className="text-center p-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                {attendee
                    .filter((itm: any) => itm.meetConfirm === 'Yes' || itm.isPresent)
                    .slice()
                    .sort((a: any, b: any) => {
                      const nameA = (a?.memberDetails?.fullName || a?.fullName || '').toLowerCase();
                      const nameB = (b?.memberDetails?.fullName || b?.fullName || '').toLowerCase();
                      return nameA.localeCompare(nameB);
                    })
                    .map((itm: any, index: number) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="p-2">{itm?.memberDetails?.fullName || itm?.fullName || 'Guest'}</td>
                      <td className="p-2">{itm?.email || itm?.memberDetails?.email}</td>
                      <td className="p-2 text-center">
                        <span className={`px-2 py-1 rounded text-xs ${
                          getRequestStatus(itm?.email || itm?.memberDetails?.email) === 'connect_with_members' ? 'bg-blue-100 text-blue-700' :
                          getRequestStatus(itm?.email || itm?.memberDetails?.email) === 'not_interested' ? 'bg-red-100 text-red-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {getRequestStatus(itm?.email || itm?.memberDetails?.email) === 'connect_with_members' ? 'Requested Specific' :
                          getRequestStatus(itm?.email || itm?.memberDetails?.email) === 'not_interested' ? 'Not Available' :
                          'Available'}
                        </span>
                      </td>
                      <td className="p-2 text-center">
                        <button 
                          onClick={() => {
                            setSelectedAttendeeForRequest(itm);
                          }}
                          className="px-3 py-1 rounded text-xs bg-[#EF7A2B] text-white hover:bg-orange-600"
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {attendee.filter((itm: any) => itm.meetConfirm === 'Yes' || itm.isPresent).length === 0 && (
                <div className="text-center py-4 text-gray-500">No checked-in or RSVP'd attendees</div>
              )}
            </div>
          </>}
          result={e => {
            setShowManagerRequestModal(false);
            setSelectedAttendeeForRequest(null);
          }}
        />
      </> : <></>}

      {showSubmitThankYou && id ? <>
        <Modal
          title={`Submit ${testimonialLabel} Request`}
          body={<>
            <div className="flex flex-col gap-4">
              <p className="text-sm text-gray-600">Submit a {testimonialLabel.toLowerCase()} request. Your group facilitator will review it during the meeting.</p>
              <div>
                <label className="block text-sm font-medium mb-1">Who are you thanking?</label>
                <select
                  className="w-full border rounded-lg p-2 text-sm"
                  value={tyReceiverId}
                  onChange={async (e) => {
                    const rid = e.target.value;
                    setTyReceiverId(rid);
                    setTySharedGroups([]);
                    setTySelectedGroupIds(data?.groupId?._id ? [data.groupId._id] : []);
                    if (rid) {
                      const res = await ApiClient.get('api/testimonial/shared-groups', { receiverId: rid });
                      if (res.success && res.data) {
                        setTySharedGroups(res.data);
                        setTySelectedGroupIds(data?.groupId?._id ? [data.groupId._id] : []);
                      }
                    }
                  }}
                >
                  <option value="">Select member...</option>
                  {attendee
                    .filter((a: any) => {
                      const memberId = a.memberDetails?._id || a.userId;
                      return memberId && memberId !== user?._id && (a.meetConfirm === 'Yes' || a.isPresent);
                    })
                    .sort((a: any, b: any) => {
                      const nameA = (a.memberDetails?.fullName || a.fullName || '').toLowerCase();
                      const nameB = (b.memberDetails?.fullName || b.fullName || '').toLowerCase();
                      return nameA.localeCompare(nameB);
                    })
                    .map((a: any) => (
                      <option key={a.memberDetails?._id || a.userId} value={a.memberDetails?._id || a.userId}>
                        {a.memberDetails?.fullName || a.fullName}{a.type === 'guest' ? ' (guest)' : ''}
                      </option>
                    ))}
                </select>
              </div>
              {tySharedGroups.length > 1 && (
                <div>
                  <label className="block text-sm font-medium mb-1">Also submit to shared groups (optional)</label>
                  <div className="space-y-1">
                    {tySharedGroups.map((g: any) => {
                      const isCurrentGroup = g._id === data?.groupId?._id;
                      const isChecked = tySelectedGroupIds.includes(g._id);
                      return (
                        <label key={g._id} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={isCurrentGroup}
                            onChange={() => {
                              if (isCurrentGroup) return;
                              setTySelectedGroupIds(prev =>
                                prev.includes(g._id) ? prev.filter(id => id !== g._id) : [...prev, g._id]
                              );
                            }}
                          />
                          <span>{g.name}{isCurrentGroup ? ' (this event)' : ''}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Type (optional)</label>
                <select className="w-full border rounded-lg p-2 text-sm" value={tyType} onChange={(e) => setTyType(e.target.value)}>
                  <option value="">Select type...</option>
                  <option value="referral">Referral</option>
                  <option value="introduction">Introduction</option>
                  <option value="business_outcome">Business Outcome</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium">Note (optional)</label>
                  {tyNote && (
                    <button type="button" onClick={() => setTyNote('')} className="text-xs text-red-500 hover:text-red-700">Clear</button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mb-1">A quick note about why you're thanking this person.</p>
                <textarea
                  className="w-full border rounded-lg p-2 text-sm"
                  value={tyNote}
                  onChange={(e) => setTyNote(e.target.value)}
                  placeholder="e.g. introduced me to a client who hired me"
                  rows={tyNote.length > 120 ? 8 : 3}
                />
              </div>
              <button
                disabled={!tyReceiverId || tySubmitting}
                className="bg-[#22C55E] text-white px-4 py-2 rounded-lg disabled:opacity-50 hover:bg-green-600 text-sm"
                onClick={async () => {
                  setTySubmitting(true);
                  const groupIdsToSend = tySelectedGroupIds.length > 0 ? tySelectedGroupIds : [data?.groupId?._id];
                  const res = await ApiClient.post('api/testimonial/submit', {
                    receivedById: tyReceiverId,
                    groupId: data?.groupId?._id,
                    groupIds: groupIdsToSend,
                    eventId: id,
                    thankYouType: tyType || null,
                    note: tyNote || null
                  });
                  setTySubmitting(false);
                  if (res.success) {
                    toast.success(`${testimonialLabel} request submitted successfully`);
                    setTyReceiverId('');
                    setTyType('');
                    setTyNote('');
                    setTySharedGroups([]);
                    setTySelectedGroupIds([]);
                    setShowSubmitThankYou(false);
                  }
                }}
              >
                {tySubmitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </>}
          result={e => {
            setShowSubmitThankYou(false);
          }}
        />
      </> : <></>}
      {showOwnVoice ? <>
        <Modal
          title="Write in Your Own Voice"
          body={<>
            <div className="flex flex-col gap-4">
              <p className="text-sm text-gray-600">
                Have your {testimonialLabel.toLowerCase()} written in <strong>your own voice</strong>. Upload up to 2 writing
                samples and/or paste some of your own writing. The AI uses these as a style reference.
              </p>
              <div className="relative">
                {/* Paywall overlay — boxes are visible but locked until subscribed. Kept lightly tinted
                    so the text behind stays readable. */}
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-white/40 rounded-lg text-center px-4">
                  <FaLock className="text-2xl text-gray-700" />
                  <p className="text-sm font-semibold text-gray-900">Premium feature — $2/month</p>
                  <p className="text-xs text-gray-700">Subscribe to upload samples and write in your own voice.</p>
                  <button
                    type="button"
                    onClick={handleSubscribeOwnVoice}
                    className="mt-1 bg-[#EF7A2B] text-white text-sm px-4 py-2 rounded hover:bg-orange-600 shadow"
                  >
                    Subscribe – $2/month
                  </button>
                </div>
                {/* The boxes — shown at full darkness (readable), but not interactive without a subscription. */}
                <div className="pointer-events-none select-none space-y-3 p-1">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">Writing samples (up to 2 files)</label>
                    <input type="file" multiple disabled className="text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">Or paste your writing</label>
                    <textarea
                      disabled
                      rows={5}
                      maxLength={5000}
                      placeholder="Paste a few paragraphs of your own writing…"
                      className="w-full border rounded-lg p-2 text-sm"
                    />
                    <p className="text-xs text-gray-400 mt-1">Up to 5,000 characters.</p>
                  </div>
                </div>
              </div>
            </div>
          </>}
          result={() => setShowOwnVoice(false)}
        />
      </> : <></>}
      {showThankYouModal && id ? <>
        <Modal
          title={`Manage ${testimonialLabelPlural}`}
          body={<>
            <ThankYouManager
              eventId={id}
              groupId={data?.groupId?._id || ''}
              attendees={attendee}
              user={user}
              label={testimonialLabelPlural}
              onClose={() => setShowThankYouModal(false)}
            />
          </>}
          result={e => {
            setShowThankYouModal(false);
          }}
        />
      </> : <></>}
      {selectedAttendeeForRequest ? <>
        <Modal
          title={`${connectMeetLabel} Request for ${selectedAttendeeForRequest?.memberDetails?.fullName || selectedAttendeeForRequest?.fullName}`}
          body={<>
            <ManagerRequestForm 
              attendee={selectedAttendeeForRequest}
              allAttendees={attendee}
              eventId={id}
              groupId={data?.groupId?._id}
              user={user}
              onComplete={() => {
                setSelectedAttendeeForRequest(null);
                getAttendee();
                // Refresh the request statuses
                ApiClient.get("api/group-request/list", { eventId: id }).then(res => {
                  if (res?.success && res?.data) {
                    const statusMap: {[email: string]: string} = {};
                    res.data.forEach((req: any) => {
                      if (req.requestedBy) {
                        statusMap[req.requestedBy] = req.requestType || 'system_generate';
                      }
                    });
                    setAttendeeRequestStatuses(statusMap);
                  }
                });
              }}
              connectMeetLabel={connectMeetLabel}
            />
          </>}
          result={e => {
            setSelectedAttendeeForRequest(null);
          }}
        />
      </> : <></>}

    </>
  );
};

export default EventDetail;
